import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Copy, Check, CheckCircle, RefreshCw, Wand2, Search, Clock, Globe, Target, AlertCircle, RotateCcw, Image as ImageIcon, Sparkles, BarChart3 } from 'lucide-react';
import { BrandKit } from '../types';
import googleDocsService from '../services/googleDocsService';
import { API_ENDPOINTS, buildApiUrl, API_BASE_URL } from '../config/api';
import { makeApiRequest } from '../utils/apiHelpers';
import { sanitizeHTMLForDisplay } from '../utils/htmlSanitizer';
import {
  generateQuizCTAs,
  generateQuizUtmUrl,
  getQuizCtaHtml,
  getQuizCtaStyles,
  countQuizCTAs,
  clearQuizCTAs,
  QUIZ_BUTTON_OPTIONS,
  QuizCtaVariant,
  GeneratedQuizCTAs,
} from '../utils/quizCtaGenerator';
import QuizCtaPreviewCards from './QuizCtaPreviewCards';
import { MODEL_DISPLAY_NAMES } from '../config/aiModels';

interface PAARowBasic {
  id: string;
  question: string;
  treeName: string;
  treeSlug: string;
  status: 'idle' | 'queued' | 'running' | 'completed' | 'error';
  output?: string;
  seoTitle?: string;
  seoDescription?: string;
  createdAt: string;
  metadata?: {
    seoTitle: string;
    seoDescription: string;
    word_count: number;
    seo_optimized: boolean;
    aeo_optimized: boolean;
    product_links_included: boolean;
    blog_link_included: boolean;
  };
  generationResult?: any;
  workflowDetails?: {
    contentGeneration?: any;
  };
}

interface PAAContentActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: PAARowBasic;
  brandKit?: BrandKit | null;
  onContentUpdate?: (rowId: string, newContent: string) => void;
}

/**
 * GeneratedImage interface
 * Why this matters: Defines structure for AI-generated images (Nano Banana Pro)
 */
interface GeneratedImage {
  base64Data: string;
  mimeType: string;
  altText: string;
  placement: 'beginning' | 'middle' | 'ending';
  imageUrl?: string;   // File URL for HTML insertion (from backend storage)
  filename?: string;   // Actual filename on disk
}

/**
 * Variables Menu Component (duplicated from BlogContentActionModal for UI parity)
 * Why this matters: Provides a searchable popup for inserting brand variables into prompts,
 * matching the Blog modal UX to reduce cognitive load.
 */
const VariablesMenu: React.FC<{
  showVariablesMenu: boolean;
  variablesButtonPosition: { top: number; left: number } | null;
  variablesMenuRef: React.RefObject<HTMLDivElement | null>;
  activePromptField: 'system' | 'user';
  variableSearchTerm: string;
  setVariableSearchTerm: (term: string) => void;
  setShowVariablesMenu: (show: boolean) => void;
  brandKit: BrandKit | null;
  insertVariable: (value: string) => void;
}> = ({
  showVariablesMenu,
  variablesButtonPosition,
  variablesMenuRef,
  activePromptField,
  variableSearchTerm,
  setVariableSearchTerm,
  setShowVariablesMenu,
  brandKit,
  insertVariable
}) => {
  if (!showVariablesMenu || !variablesButtonPosition) return null;

  // Build variables list including custom variables
  const getAllVariables = () => {
    const standardVariables = [
      { key: 'url', label: 'Brand URL', value: '{{ brand_kit.url }}' },
      { key: 'about_brand', label: 'About Brand', value: '{{ brand_kit.about_brand }}' },
      { key: 'ideal_customer_profile', label: 'Ideal Customer Profile', value: '{{ brand_kit.ideal_customer_profile }}' },
      { key: 'competitors', label: 'Competitors', value: '{{ brand_kit.competitors }}' },
      { key: 'brand_point_of_view', label: 'Brand Point of View', value: '{{ brand_kit.brand_point_of_view }}' },
      { key: 'author_persona', label: 'Author Persona', value: '{{ brand_kit.author_persona }}' },
      { key: 'tone_of_voice', label: 'Tone of Voice', value: '{{ brand_kit.tone_of_voice }}' },
      { key: 'header_case_type', label: 'Header Case Type', value: '{{ brand_kit.header_case_type }}' },
      { key: 'writing_rules', label: 'Writing Rules', value: '{{ brand_kit.writing_rules }}' },
      { key: 'cta_text', label: 'CTA Text', value: '{{ brand_kit.cta_text }}' },
      { key: 'cta_destination', label: 'CTA Destination', value: '{{ brand_kit.cta_destination }}' },
      // Competitor-conquesting extras (empty by default, can be filled upstream)
      { key: 'research_key_insights', label: 'Research Insights', value: '{{ brand_kit.research_key_insights }}' },
      { key: 'gap_analysis_summary', label: 'Gap Analysis Summary', value: '{{ brand_kit.gap_analysis_summary }}' },
      { key: 'primary_angle', label: 'Primary Angle', value: '{{ brand_kit.primary_angle }}' },
      { key: 'structure_recommendations', label: 'Structure Recs', value: '{{ brand_kit.structure_recommendations }}' },
      { key: 'seo_suggestions', label: 'SEO Suggestions', value: '{{ brand_kit.seo_suggestions }}' }
    ];

    const customVariables = brandKit && brandKit.customVariables
      ? Object.keys(brandKit.customVariables).map((key) => ({
          key,
          label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          value: `{{ brand_kit.${key} }}`
        }))
      : [];

    return [...customVariables, ...standardVariables];
  };

  const filtered = (() => {
    const all = getAllVariables();
    if (!variableSearchTerm) return all;
    return all.filter(
      (v) =>
        v.label.toLowerCase().includes(variableSearchTerm.toLowerCase()) ||
        v.key.toLowerCase().includes(variableSearchTerm.toLowerCase())
    );
  })();

  return (
    <div
      ref={variablesMenuRef}
      className="content-variables-menu"
      style={{
        position: 'fixed',
        top: variablesButtonPosition.top,
        left: variablesButtonPosition.left,
        backgroundColor: 'white',
        border: '0.0625rem solid #e5e7eb',
        borderRadius: '0.75rem',
        boxShadow: '0 1.5625rem 3.125rem -0.75rem rgba(0, 0, 0, 0.25)',
        zIndex: 10000,
        width: '25rem',
        maxHeight: '37.5rem',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ padding: '1.5rem 1.5rem 1rem', borderBottom: '1px solid #f3f4f6', background: '#fafafa' }} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, color: '#111827' }}>Brand Variables</h3>
          <button
            onClick={() => setShowVariablesMenu(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', borderRadius: '0.25rem' }}
          >
            <X size={20} />
          </button>
        </div>
        <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>Insert into {activePromptField} prompt</p>
      </div>

      <div style={{ padding: '1rem 1.5rem' }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search variables..."
            value={variableSearchTerm}
            onChange={(e) => setVariableSearchTerm(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              padding: '0.625rem 0.625rem 0.625rem 2.5rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 1.5rem 1.5rem' }}>
        {filtered.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {filtered.map((variable) => (
              <div
                key={variable.key}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); insertVariable(variable.value); }}
                style={{
                  padding: '1rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem', color: '#374151' }}>{variable.label}</div>
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: '#7c3aed',
                    backgroundColor: '#f3f4f6',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    border: '1px solid #e5e7eb',
                    fontWeight: 600
                  }}
                >
                  {variable.value}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
            <Search size={32} style={{ margin: '0 auto 1rem', opacity: 0.3, display: 'block' }} />
            <p style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>No variables found</p>
            <p style={{ fontSize: '0.75rem', margin: 0 }}>Try adjusting your search terms</p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * PAAContentActionModal
 * Why this matters: Provides content viewing and editing tools for PAA FAQ content generation
 * with SEO metadata display (SEO Title and SEO Description) and all power-user features.
 */
const PAAContentActionModal: React.FC<PAAContentActionModalProps> = ({
  isOpen,
  onClose,
  row,
  brandKit = null,
  onContentUpdate
}) => {
  // Prompts & brand kit
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [userPrompt, setUserPrompt] = useState<string>('');
  const [resolvedBrandKit, setResolvedBrandKit] = useState<BrandKit | null>(brandKit || null);
  
  // Store workflow data (deep research and gap analysis) from previous generation
  const [workflowData, setWorkflowData] = useState<{
    deep_research?: any;
    gap_analysis?: any;
  } | null>(null);

  // UI states
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [showVariablesMenu, setShowVariablesMenu] = useState<boolean>(false);
  const [activePromptField, setActivePromptField] = useState<'system' | 'user'>('system');
  const [variableSearchTerm, setVariableSearchTerm] = useState<string>('');
  const [variablesButtonPosition, setVariablesButtonPosition] = useState<{ top: number; left: number } | null>(null);

  // Content & editing
  const [generatedContent, setGeneratedContent] = useState<string>('');
  const [isEditingContent, setIsEditingContent] = useState<boolean>(false);
  const [editableContent, setEditableContent] = useState<string>('');
  // SEO fields and copy UI state (FAQ-specific)
  const [seoTitle, setSeoTitle] = useState<string>('');
  const [seoDescription, setSeoDescription] = useState<string>('');
  const [showSeoTitleCopied, setShowSeoTitleCopied] = useState<boolean>(false);
  const [showSeoDescCopied, setShowSeoDescCopied] = useState<boolean>(false);
  // Auto-save prompts UI state
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saving' | 'saved' | ''>('');
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [hasUserInput, setHasUserInput] = useState<boolean>(false);
  // Sheets UX
  const [isOpeningSheets, setIsOpeningSheets] = useState<boolean>(false);
  const [showSheetsMessage, setShowSheetsMessage] = useState<boolean>(false);
  const [sheetsSuccessMessage, setSheetsSuccessMessage] = useState<string>('');

  // CMS Integration states
  const [showCMSModal, setShowCMSModal] = useState(false);
  const [showCustomCMSForm, setShowCustomCMSForm] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [publishResult, setPublishResult] = useState<any>(null);
  const [showComingSoonMessage, setShowComingSoonMessage] = useState<string | null>(null);
  const [customCMSConfig, setCustomCMSConfig] = useState({
    shop_name: '',
    access_token: '',
    blog_id: '',
    cms_type: 'shopify'
  });
  const [showValidationError, setShowValidationError] = useState(false);
  const [showGoogleClientIdError, setShowGoogleClientIdError] = useState(false);
  
  // Contextual CTA states
  const [enableContextualCtas, setEnableContextualCtas] = useState(true);
  const [isEnhancingWithCtas, setIsEnhancingWithCtas] = useState(false);
  const [ctaEnhancementResult, setCtaEnhancementResult] = useState<any>(null);

  // CTA Generation states (copied from BlogContentActionModal)
  const [generatedCTAs, setGeneratedCTAs] = useState<GeneratedQuizCTAs | null>(null);
  const [isGeneratingCTAs, setIsGeneratingCTAs] = useState(false);
  const [ctaError, setCtaError] = useState('');
  const [ctaGenerationStage, setCtaGenerationStage] = useState('');
  const [showCtaSkeletons, setShowCtaSkeletons] = useState(false);
  const [ctaCopySuccess, setCtaCopySuccess] = useState<string | null>(null);
  const [vocKitReady, setVocKitReady] = useState(false);
  const [vocKitReadyDismissed, setVocKitReadyDismissed] = useState(false);
  const [painPointsCount, setPainPointsCount] = useState(0);
  const [quizVariantIndex, setQuizVariantIndex] = useState(() => {
    try {
      const saved = localStorage.getItem(`quiz_ctas_${row.id}`);
      if (saved) return JSON.parse(saved).variantIndex ?? 0;
    } catch {}
    return 0;
  });

  // Image Generation States (Nano Banana Pro)
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [regeneratingPlacement, setRegeneratingPlacement] = useState<string | null>(null);
  const [imageError, setImageError] = useState('');
  const [imageCopySuccess, setImageCopySuccess] = useState<string>('');

  // Infographic Generation States
  const [generatedInfographic, setGeneratedInfographic] = useState<GeneratedImage | null>(null);
  const [isGeneratingInfographic, setIsGeneratingInfographic] = useState(false);
  const [infographicError, setInfographicError] = useState('');
  const [infographicCopySuccess, setInfographicCopySuccess] = useState<string>('');

  // localStorage keys for persisting images per tree
  const imageStorageKey = `citrus-images-${row.treeSlug}`;
  const infographicStorageKey = `citrus-infographic-${row.treeSlug}`;

  // Edit history functionality for CTA insertion (copied from BlogContentActionModal)
  const [editHistory, setEditHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Refs
  const systemRef = useRef<HTMLTextAreaElement | null>(null);
  const userRef = useRef<HTMLTextAreaElement | null>(null);
  const variablesMenuRef = useRef<HTMLDivElement | null>(null);
  const systemVariablesButtonRef = useRef<HTMLButtonElement | null>(null);
  const userVariablesButtonRef = useRef<HTMLButtonElement | null>(null);
  const editableContentRef = useRef<HTMLTextAreaElement | null>(null);
  const rightPanelRef = useRef<HTMLDivElement | null>(null);

  /**
   * Generate Images using Gemini (Nano Banana Pro)
   * Why this matters: Creates 3 custom images for the FAQ article
   */
  const generateImages = async () => {
    setIsGeneratingImages(true);
    setImageError('');
    setGeneratedImages([]); // Clear previous
    
    try {
      const response = await makeApiRequest(API_ENDPOINTS.citrusTreeGenerateImages, {
        method: 'POST',
        body: JSON.stringify({
          treeName: row.treeName,
          content: generatedContent || editableContent
        })
      });
      
      if (response.success && response.data?.images) {
        setGeneratedImages(response.data.images);
      } else {
        setImageError('Failed to generate images. Please try again.');
      }
    } catch (error: any) {
      console.error('Image generation failed:', error);
      setImageError(error.message || 'Failed to generate images');
    } finally {
      setIsGeneratingImages(false);
    }
  };

  /**
   * Regenerate a single image
   * Why this matters: Allows user to regenerate just one image they don't like
   */
  const regenerateSingleImage = async (placement: string) => {
    setRegeneratingPlacement(placement);
    setImageError('');
    
    try {
      const response = await makeApiRequest(API_ENDPOINTS.citrusTreeGenerateImages, {
        method: 'POST',
        body: JSON.stringify({
          treeName: row.treeName,
          content: generatedContent || editableContent,
          singlePlacement: placement
        })
      });
      
      if (response.success && response.data?.images) {
        const newImage = response.data.images.find((img: GeneratedImage) => img.placement === placement);
        if (newImage) {
          setGeneratedImages(prev => prev.map(img => 
            img.placement === placement ? newImage : img
          ));
        }
      } else {
        setImageError(`Failed to regenerate ${placement} image.`);
      }
    } catch (error: any) {
      console.error('Single image regeneration failed:', error);
      setImageError(error.message || `Failed to regenerate ${placement} image`);
    } finally {
      setRegeneratingPlacement(null);
    }
  };

  /**
   * Copy Image HTML to clipboard
   * Why this matters: Allows user to manually insert image into external editors
   */
  const copyImageHtml = (image: GeneratedImage) => {
    const imageSrc = image.imageUrl 
      ? (image.imageUrl.startsWith('http') ? image.imageUrl : `${API_BASE_URL}${image.imageUrl}`)
      : `data:${image.mimeType};base64,${image.base64Data}`;
    
    const html = `<figure style="margin: 2rem 0; text-align: center;">
  <img src="${imageSrc}" alt="${image.altText}" style="max-width: 100%; height: auto; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />
  <figcaption style="margin-top: 0.75rem; font-size: 0.9rem; color: #666; font-style: italic;">${image.altText}</figcaption>
</figure>`;
    
    navigator.clipboard.writeText(html).then(() => {
      setImageCopySuccess(image.placement);
      setTimeout(() => setImageCopySuccess(''), 2000);
    });
  };

  /**
   * Insert Image HTML at cursor position in editor
   * Why this matters: Allows direct insertion of generated images into content at cursor position
   */
  const insertImageAtCursor = (image: GeneratedImage) => {
    if (!editableContentRef.current || !isEditingContent) {
      setImageError('Please enter Edit Mode first, then click where you want to insert the image.');
      setTimeout(() => setImageError(''), 3000);
      return;
    }

    const textarea = editableContentRef.current;
    const cursorPosition = textarea.selectionStart;
    const scrollTop = textarea.scrollTop;
    const scrollLeft = textarea.scrollLeft;
    
    let imageSrc: string;
    if (image.imageUrl) {
      imageSrc = image.imageUrl.startsWith('http') ? image.imageUrl : `${API_BASE_URL}${image.imageUrl}`;
    } else {
      imageSrc = `data:${image.mimeType};base64,${image.base64Data}`;
    }
    
    const imageHtml = `<figure style="margin: 2rem 0; text-align: center;">
  <img src="${imageSrc}" alt="${image.altText}" style="max-width: 100%; height: auto; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />
  <figcaption style="margin-top: 0.75rem; font-size: 0.9rem; color: #666; font-style: italic;">${image.altText}</figcaption>
</figure>`;
    
    const beforeCursor = editableContent.substring(0, cursorPosition);
    const afterCursor = editableContent.substring(cursorPosition);
    const imageWithComment = `\n\n<!-- ${image.placement.charAt(0).toUpperCase() + image.placement.slice(1)} Image -->\n${imageHtml}\n<!-- End ${image.placement.charAt(0).toUpperCase() + image.placement.slice(1)} Image -->\n\n`;
    
    const newContent = beforeCursor + imageWithComment + afterCursor;
    
    // Save to history for undo
    setEditHistory(prev => [...prev.slice(0, historyIndex + 1), newContent]);
    setHistoryIndex(prev => prev + 1);
    setEditableContent(newContent);
    
    setTimeout(() => {
      const newCursorPosition = cursorPosition + imageWithComment.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      textarea.scrollTop = scrollTop;
      textarea.scrollLeft = scrollLeft;
    }, 50);

    setImageCopySuccess(`${image.placement}_inserted`);
    setTimeout(() => setImageCopySuccess(''), 2000);
    console.log(`🖼️ Inserted ${image.placement} image at cursor position ${cursorPosition}`);
  };

  /**
   * Generate Infographic using Gemini (Nano Banana Pro)
   * Why this matters: Creates a branded infographic summarizing the FAQ article
   */
  const generateInfographic = async () => {
    setIsGeneratingInfographic(true);
    setInfographicError('');
    setGeneratedInfographic(null);
    
    try {
      const response = await makeApiRequest(API_ENDPOINTS.citrusTreeGenerateInfographic, {
        method: 'POST',
        body: JSON.stringify({
          treeName: row.treeName
        })
      });
      
      if (response.success && response.data?.infographic) {
        setGeneratedInfographic(response.data.infographic);
      } else {
        setInfographicError('Failed to generate infographic. Please try again.');
      }
    } catch (error: any) {
      console.error('Infographic generation failed:', error);
      setInfographicError(error.message || 'Failed to generate infographic');
    } finally {
      setIsGeneratingInfographic(false);
    }
  };

  /**
   * Copy Infographic HTML to clipboard
   * Why this matters: Allows user to manually insert infographic into the editor
   */
  const copyInfographicHtml = () => {
    if (!generatedInfographic) return;
    
    const imageSrc = generatedInfographic.imageUrl 
      ? (generatedInfographic.imageUrl.startsWith('http') ? generatedInfographic.imageUrl : `${API_BASE_URL}${generatedInfographic.imageUrl}`)
      : `data:${generatedInfographic.mimeType};base64,${generatedInfographic.base64Data}`;
    
    const html = `<figure style="margin: 2rem 0; text-align: center;">
  <img src="${imageSrc}" alt="${generatedInfographic.altText}" style="max-width: 100%; height: auto; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />
  <figcaption style="margin-top: 0.75rem; font-size: 0.9rem; color: #666; font-style: italic;">${generatedInfographic.altText}</figcaption>
</figure>`;
    
    navigator.clipboard.writeText(html).then(() => {
      setInfographicCopySuccess('copied');
      setTimeout(() => setInfographicCopySuccess(''), 2000);
    });
  };

  /**
   * Insert Infographic HTML at cursor position in editor
   * Why this matters: Allows direct insertion of infographic into content at cursor position
   */
  const insertInfographicAtCursor = () => {
    if (!generatedInfographic) return;
    
    if (!editableContentRef.current || !isEditingContent) {
      setInfographicError('Please enter Edit Mode first, then click where you want to insert the infographic.');
      setTimeout(() => setInfographicError(''), 3000);
      return;
    }

    const textarea = editableContentRef.current;
    const cursorPosition = textarea.selectionStart;
    const scrollTop = textarea.scrollTop;
    const scrollLeft = textarea.scrollLeft;
    
    let imageSrc: string;
    if (generatedInfographic.imageUrl) {
      imageSrc = generatedInfographic.imageUrl.startsWith('http') ? generatedInfographic.imageUrl : `${API_BASE_URL}${generatedInfographic.imageUrl}`;
    } else {
      imageSrc = `data:${generatedInfographic.mimeType};base64,${generatedInfographic.base64Data}`;
    }
    
    const infographicHtml = `<figure style="margin: 2rem 0; text-align: center;">
  <img src="${imageSrc}" alt="${generatedInfographic.altText}" style="max-width: 100%; height: auto; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />
  <figcaption style="margin-top: 0.75rem; font-size: 0.9rem; color: #666; font-style: italic;">${generatedInfographic.altText}</figcaption>
</figure>`;
    
    const beforeCursor = editableContent.substring(0, cursorPosition);
    const afterCursor = editableContent.substring(cursorPosition);
    const infographicWithComment = `\n\n<!-- Infographic -->\n${infographicHtml}\n<!-- End Infographic -->\n\n`;
    
    const newContent = beforeCursor + infographicWithComment + afterCursor;
    
    // Save to history for undo
    setEditHistory(prev => [...prev.slice(0, historyIndex + 1), newContent]);
    setHistoryIndex(prev => prev + 1);
    setEditableContent(newContent);
    
    setTimeout(() => {
      const newCursorPosition = cursorPosition + infographicWithComment.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      textarea.scrollTop = scrollTop;
      textarea.scrollLeft = scrollLeft;
    }, 50);

    setInfographicCopySuccess('inserted');
    setTimeout(() => setInfographicCopySuccess(''), 2000);
    console.log(`📊 Inserted infographic at cursor position ${cursorPosition}`);
  };

  /**
   * generateAIMetaFields
   * Why this matters: Produces meta SEO fields so this modal mirrors Blog modal UX.
   */
  function generateAIMetaFields(
    keyword: string,
    content: string
  ): Promise<{ seoTitle: string; seoDescription: string }> {
    return (async () => {
      try {
        const contentPreview = content.replace(/<[^>]*>/g, '').substring(0, 500);
        const apiUrl = API_ENDPOINTS.generateMeta;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            keyword,
            content_preview: contentPreview,
            prompt: `Generate meta fields for competitor content about "${keyword}".`
          })
        });
        if (!response.ok) throw new Error('Meta API failed');
        const data = await response.json();
        return {
          seoTitle: data.seoTitle || generateFallbackTitle(keyword),
          seoDescription: data.seoDescription || generateFallbackDescription(keyword)
        };
      } catch {
        return {
          seoTitle: generateFallbackTitle(keyword),
          seoDescription: generateFallbackDescription(keyword)
        };
      }
    })();
  }

  /**
   * Fallback meta helpers
   * Why this matters: Guarantees fields even if AI meta API fails.
   */
  function generateFallbackTitle(keyword: string): string {
    const k = keyword.trim();
    // AEO-optimized fallback: question format or clear topic statement
    // Why this matters: AI engines prefer clear, intent-matching titles without hyperbole
    console.log('⚠️ Using fallback title generation for keyword:', k);
    if (k.toLowerCase().startsWith('what') || k.toLowerCase().startsWith('how')) {
      return `${k.charAt(0).toUpperCase() + k.slice(1)} | US Citrus`;
    }
    // Use more varied, natural formats
    const formats = [
      `${k.charAt(0).toUpperCase() + k.slice(1)} for B2B Teams | US Citrus`,
      `Understanding ${k.charAt(0).toUpperCase() + k.slice(1)} in Sales | US Citrus`,
      `${k.charAt(0).toUpperCase() + k.slice(1)}: Practical Guide | US Citrus`
    ];
    return formats[Math.floor(Math.random() * formats.length)];
  }
  function generateFallbackDescription(keyword: string): string {
    // AEO-optimized fallback: direct value statement without made-up claims
    // Why this matters: AI engines penalize unverifiable claims and prefer factual descriptions
    const k = keyword.trim().toLowerCase();
    console.log('⚠️ Using fallback description generation for keyword:', k);
    // Use more varied, natural descriptions
    const templates = [
      `Explore ${k} strategies and implementation approaches for B2B sales teams. US Citrus provides tools and insights to optimize your ${k} processes.`,
      `Practical ${k} techniques for sales organizations. See how US Citrus's platform enables teams to implement and scale ${k} effectively.`,
      `${k.charAt(0).toUpperCase() + k.slice(1)} fundamentals and best practices for modern sales teams. US Citrus helps streamline ${k} workflows and drive results.`
    ];
    // Note: For SEO, descriptions should ideally be 150-160 characters, but we show full content in UI for user review
    return templates[Math.floor(Math.random() * templates.length)];
  }

  /**
   * Get random CTA anchor text to ensure even distribution
   * Why this matters: Prevents LLM bias toward first option in the list
   */
  const getRandomCTAAnchorText = (): string => {
    const ctaOptions = [
      "Start Your Free Trial",
      "Browse Our Trees", 
      "Start a Trial",
      "Schedule a Demo",
      "Request a Demo", 
      "Start Prospecting",
      "Get Leads Now"
    ];
    
    // Use random selection to ensure even distribution
    const randomIndex = Math.floor(Math.random() * ctaOptions.length);
    const selectedCTA = ctaOptions[randomIndex];
    console.log(`🎯 [CompetitorModal] Selected CTA anchor text: "${selectedCTA}" (${randomIndex + 1}/${ctaOptions.length})`);
    return selectedCTA;
  };

  // Load initial content from row
  // Why this matters: Always prioritizes fresh content from row.output over any cached content
  // This ensures users see the latest generated content without truncation issues
  useEffect(() => {
    if (!isOpen) return;
    
    // Always prioritize fresh content from row.output over cached content
    // This ensures users see the latest generated content with proper US Citrus branding
    const content = row.output || '';
    
    // Debug logging to track content length and potential truncation
    console.log('🔍 [PAAContentActionModal] Content initialization:', {
      rowId: row.id,
      question: row.question,
      treeName: row.treeName,
      rawContentLength: content.length,
      rawContentPreview: content.substring(0, 200),
      rawContentEnd: content.length > 200 ? '...' + content.substring(content.length - 200) : content,
      hasTruncationMarker: content.includes('[truncated]') || content.includes('...'),
      contentEndsWithTruncation: content.trim().endsWith('...') || content.trim().endsWith('[truncated]')
    });
    
    if (content.trim().length > 0) {
      // Clean the content to remove JSON artifacts and escape sequences
      const cleanedContent = cleanAIContent(content);
      
      // Log after cleaning to detect if cleaning is truncating
      console.log('🔍 [PAAContentActionModal] After cleaning:', {
        cleanedLength: cleanedContent.length,
        lengthDifference: content.length - cleanedContent.length,
        cleanedPreview: cleanedContent.substring(0, 200),
        cleanedEnd: cleanedContent.length > 200 ? '...' + cleanedContent.substring(cleanedContent.length - 200) : cleanedContent
      });
      
      setGeneratedContent(cleanedContent);
      setEditableContent(cleanedContent);
      setIsEditingContent(false);
      
      console.log('📊 Initializing PAAContentActionModal with fresh content for question:', row.question);
      console.log('🔄 Fresh content length:', content.length, 'characters');
      console.log('🔄 Cleaned content length:', cleanedContent.length, 'characters');
      
      // Check if meta fields are already available (from row.metadata or localStorage)
      // Why this matters: Avoids redundant API calls and prevents modal lag
      const existingSeoTitle = row.metadata?.seoTitle || row.seoTitle || '';
      const existingSeoDesc = row.metadata?.seoDescription || row.seoDescription || '';
      
      if (existingSeoTitle || existingSeoDesc) {
        console.log('✅ Loading existing SEO fields from row.metadata');
        setSeoTitle(existingSeoTitle);
        setSeoDescription(existingSeoDesc);
      } else {
        // Try loading from localStorage as fallback
        try {
          const savedMeta = localStorage.getItem(`us_citrus_paa_meta_${row.id}`);
          if (savedMeta) {
            const { seoTitle: savedTitle, seoDescription: savedDesc } = JSON.parse(savedMeta);
            if (savedTitle || savedDesc) {
              console.log('✅ Loading existing SEO fields from localStorage');
              setSeoTitle(savedTitle || '');
              setSeoDescription(savedDesc || '');
            }
          }
        } catch (error) {
          console.log('⚠️ Could not load saved SEO fields:', error);
        }
      }
    } else {
      console.log('📝 No existing content found for row:', row.id);
    }
    
    // Load workflow data from localStorage if available
    try {
      const savedWorkflow = localStorage.getItem(`us_citrus_competitor_workflow_${row.id}`);
      if (savedWorkflow) {
        const parsedWorkflow = JSON.parse(savedWorkflow);
        console.log('📊 Loaded saved workflow data for row:', row.id);
        setWorkflowData(parsedWorkflow);
      }
    } catch (e) {
      console.error('Failed to load workflow data:', e);
    }
    
    // Load persisted quiz CTAs from localStorage
    try {
      const saved = localStorage.getItem(`quiz_ctas_${row.id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.ctas) {
          setGeneratedCTAs(parsed.ctas);
          console.log('✅ [PAAModal] Loaded saved quiz CTAs for row:', row.id);
        }
      }
    } catch (error) {
      console.error('❌ [PAAModal] Error loading saved quiz CTAs:', error);
    }
  }, [isOpen, row.output]);

  // Load brand kit if not provided via props (draft first, then saved). Keep in sync with Blog/Content modals.
  useEffect(() => {
    const loadBrandKit = () => {
      if (brandKit) {
        setResolvedBrandKit(brandKit);
        return;
      }
      try {
        const draft = localStorage.getItem('us_citrus_brand_kit_draft');
        const saved = localStorage.getItem('us_citrus_brand_kit');
        const data = draft || saved;
        if (data) {
          setResolvedBrandKit(JSON.parse(data));
        } else {
          setResolvedBrandKit(null);
        }
      } catch {
        setResolvedBrandKit(null);
      }
    };
    loadBrandKit();
  }, [brandKit]);

  // Listen for brand kit updates (same-tab custom event and storage) and refresh immediately
  useEffect(() => {
    const loadBK = () => {
      try {
        const draft = localStorage.getItem('us_citrus_brand_kit_draft');
        const saved = localStorage.getItem('us_citrus_brand_kit');
        const data = draft || saved;
        setResolvedBrandKit(data ? JSON.parse(data) : null);
      } catch {
        setResolvedBrandKit(null);
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'us_citrus_brand_kit' || e.key === 'us_citrus_brand_kit_draft') loadBK();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('us_citrus_brand_kit_updated', loadBK as any);
    // Initial sync on mount of this effect
    loadBK();
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('us_citrus_brand_kit_updated', loadBK as any);
    };
  }, []);

  // Load saved prompts if present, else keep generated defaults
  useEffect(() => {
    if (!isOpen) return;
    try {
      const saved = localStorage.getItem(`us_citrus_competitor_prompts_draft_${row.id}`);
      if (saved) {
        const { systemPrompt: savedSystem, userPrompt: savedUser } = JSON.parse(saved);
        if (typeof savedSystem === 'string') setSystemPrompt(savedSystem);
        if (typeof savedUser === 'string') setUserPrompt(savedUser);
      }
    } catch (err) {
      // ignore bad JSON
      console.warn('[CompetitorContentActionModal] Failed to load saved prompts', err);
    }
  }, [isOpen, row.id]);

  // Auto-save prompts when they change (debounced)
  useEffect(() => {
    if (!isOpen || !hasUserInput) return;
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    setAutoSaveStatus('saving');
    const timeout = setTimeout(() => {
      try {
        const promptsData = {
          systemPrompt,
          userPrompt,
          rowId: row.id,
          lastModified: new Date().toISOString()
        };
        localStorage.setItem(`us_citrus_competitor_prompts_draft_${row.id}`, JSON.stringify(promptsData));
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(''), 2000);
      } catch {
        setAutoSaveStatus('');
      }
    }, 1000);
    setAutoSaveTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [systemPrompt, userPrompt, isOpen, hasUserInput, row.id]);

  // Clear auto-save state on close
  useEffect(() => {
    if (!isOpen) {
      setAutoSaveStatus('');
      setHasUserInput(false);
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
        setAutoSaveTimeout(null);
      }
    }
  }, [isOpen, autoSaveTimeout]);

  // Load persisted images from localStorage when modal opens
  // Why this matters: Keeps generated images even after closing modal or refreshing page
  useEffect(() => {
    if (!isOpen || !row) return;
    
    try {
      const savedImages = localStorage.getItem(imageStorageKey);
      if (savedImages) {
        const parsedImages = JSON.parse(savedImages);
        if (Array.isArray(parsedImages) && parsedImages.length > 0) {
          setGeneratedImages(parsedImages);
          console.log(`🖼️ Loaded ${parsedImages.length} persisted images for ${row.treeName}`);
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not load persisted images:', error);
    }
  }, [isOpen, row, imageStorageKey]);

  // Save generated images to localStorage whenever they change
  // Why this matters: Persists images across modal closes and page refreshes
  useEffect(() => {
    if (generatedImages.length > 0) {
      try {
        // Only save essential data - not base64
        const imagesToSave = generatedImages.map(img => ({
          imageUrl: img.imageUrl,
          altText: img.altText,
          placement: img.placement,
          mimeType: img.mimeType,
        }));
        localStorage.setItem(imageStorageKey, JSON.stringify(imagesToSave));
        console.log(`💾 Saved ${generatedImages.length} images to localStorage for ${row?.treeName}`);
      } catch (error) {
        console.warn('⚠️ Could not persist images:', error);
      }
    }
  }, [generatedImages, imageStorageKey, row?.treeName]);

  // Load persisted infographic from localStorage when modal opens
  // Why this matters: Keeps generated infographic even after closing modal or refreshing page
  useEffect(() => {
    if (!isOpen || !row) return;
    
    try {
      const savedInfographic = localStorage.getItem(infographicStorageKey);
      if (savedInfographic) {
        const parsedInfographic = JSON.parse(savedInfographic);
        if (parsedInfographic && parsedInfographic.altText) {
          setGeneratedInfographic(parsedInfographic);
          console.log(`📊 Loaded persisted infographic for ${row.treeName}`);
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not load persisted infographic:', error);
    }
  }, [isOpen, row, infographicStorageKey]);

  // Save generated infographic to localStorage whenever it changes
  // Why this matters: Persists infographic across modal closes and page refreshes
  useEffect(() => {
    if (generatedInfographic) {
      try {
        // Only save essential data - not base64
        const infographicToSave = {
          imageUrl: generatedInfographic.imageUrl,
          altText: generatedInfographic.altText,
          placement: generatedInfographic.placement,
          mimeType: generatedInfographic.mimeType,
        };
        localStorage.setItem(infographicStorageKey, JSON.stringify(infographicToSave));
        console.log(`💾 Saved infographic to localStorage for ${row?.treeName}`);
      } catch (error) {
        console.warn('⚠️ Could not persist infographic:', error);
      }
    }
  }, [generatedInfographic, infographicStorageKey, row?.treeName]);

  // VoC Kit status checking (copied from BlogContentActionModal)
  useEffect(() => {
    const checkVocKit = () => {
      try {
        // Check draft first, then fallback to saved version (same as VoCKitPage)
        const draft = localStorage.getItem('us_citrus_voc_kit_draft');
        const saved = localStorage.getItem('us_citrus_voc_kit');
        const vocData = draft || saved;
        
        console.log('🔍 CompetitorContentActionModal VoC Kit Check:', {
          hasDraft: !!draft,
          hasSaved: !!saved,
          usingData: vocData ? 'found' : 'none'
        });
        
        if (vocData) {
          const parsed = JSON.parse(vocData);
          const hasAnalysis = parsed.hasGeneratedAnalysis && parsed.extractedPainPoints?.length > 0;
          
          console.log('📊 VoC Kit Data Analysis:', {
            hasGeneratedAnalysis: parsed.hasGeneratedAnalysis,
            painPointsCount: parsed.extractedPainPoints?.length || 0,
            finalVocKitReady: hasAnalysis
          });
          
          setVocKitReady(hasAnalysis);
          setPainPointsCount(parsed.extractedPainPoints?.length || 0);
        } else {
          console.log('❌ No VoC Kit data found');
          setVocKitReady(false);
          setPainPointsCount(0);
        }
      } catch (error) {
        console.error('❌ Error checking VoC Kit:', error);
        setVocKitReady(false);
        setPainPointsCount(0);
      }
    };

    const checkDismissedState = () => {
      try {
        const dismissed = localStorage.getItem('us_citrus_voc_kit_ready_dismissed');
        setVocKitReadyDismissed(dismissed === 'true');
      } catch {
        setVocKitReadyDismissed(false);
      }
    };

    if (isOpen) {
      checkVocKit();
      checkDismissedState();
    }

    const handleVocUpdate = () => checkVocKit();
    window.addEventListener('us-citrus-voc-kit-updated', handleVocUpdate);
    return () => window.removeEventListener('us-citrus-voc-kit-updated', handleVocUpdate);
  }, [isOpen]);

  /**
   * defaultSystemPrompt / defaultUserPrompt
   * Why this matters: Prompts are tailored to “beat the competitor URL” using deep research and gap analysis.
   */
  const defaultSystemPrompt = useMemo(() => {
    return `You are an elite content strategist and writer. Your goal is to generate a comprehensive, AEO-optimized article that explicitly outperforms a specific competitor page for the target keyword. You must:
- Cover all core topics the competitor covers, but with clearer structure and more depth
- Incorporate unique insights from independent deep research
- Address gaps identified by gap analysis
- Produce clean markdown with proper H1/H2/H3, tables when helpful, and inline citations [anchor](URL)
- End with a strong conclusion and clear CTA to find your perfect tree`;
  }, []);

  const defaultUserPrompt = useMemo(() => {
    // Select random CTA to prevent LLM bias
    const selectedCTA = getRandomCTAAnchorText();
    
    // Build deep research and gap analysis context if available
    let researchContext = '';
    if (workflowData?.deep_research) {
      const insights = workflowData.deep_research?.research_findings?.key_insights || [];
      if (insights.length > 0) {
        researchContext += `\nDEEP RESEARCH INSIGHTS:\n${JSON.stringify(insights, null, 2)}\n`;
      }
    }
    if (workflowData?.gap_analysis) {
      const gaps = workflowData.gap_analysis?.analysis_summary?.identified_gaps || [];
      const strategy = workflowData.gap_analysis?.content_strategy || {};
      if (gaps.length > 0 || strategy.primary_angle) {
        researchContext += `\nGAP ANALYSIS:\n- Identified Gaps: ${JSON.stringify(gaps, null, 2)}`;
        if (strategy.primary_angle) {
          researchContext += `\n- Primary Angle: ${strategy.primary_angle}`;
        }
        if (strategy.content_structure_recommendations) {
          researchContext += `\n- Structure Recommendations: ${JSON.stringify(strategy.content_structure_recommendations, null, 2)}`;
        }
        researchContext += '\n';
      }
    }
    
    const productUrl = `https://uscitrusnursery.com/products/${row.treeSlug}`;
    return `OBJECTIVE: Create comprehensive FAQ content about "${row.question}" for ${row.treeName}

PRODUCT CONTEXT: ${productUrl}
TARGET AUDIENCE: {{ brand_kit.ideal_customer_profile }}
BRAND CONTEXT: {{ brand_kit.about_brand }}
BRAND POV: {{ brand_kit.brand_point_of_view }}${researchContext ? '\n' + researchContext : ''}

CONTENT REQUIREMENTS:
1) Answer the FAQ question comprehensively and authoritatively
2) Use {{ brand_kit.tone_of_voice }} tone throughout
3) Follow these writing rules: {{ brand_kit.writing_rules }}
4) Apply {{ brand_kit.header_case_type }} for headers
5) Include practical tips and expert insights
6) Use markdown with proper headers, lists, and tables
7) Naturally link to the product page: ${productUrl}
8) End with "Getting Started with ${row.treeName}" section
9) Include CTA using this exact anchor text: "${selectedCTA}" - always link to the product page

The content should be comprehensive, authoritative, and helpful for citrus growers.`;
  }, [row.question, row.treeName, row.treeSlug]);

  /**
   * generateInitialPrompts
    * Why this matters: Ensures competitor context and deep research/gap analysis outputs are
    * explicitly injected into the prompts, and brand kit variables are required throughout.
    * Produces consistent, JSON-structured output (content + meta fields) and formatting.
   */
  const generateInitialPrompts = () => {
    const currentYear = 2025;
    // Select random CTA to prevent LLM bias
    const selectedCTA = getRandomCTAAnchorText();
    // Extract workflow data from the row (simplified for PAA - no deep research or gap analysis)
    const workflowData = row.workflowDetails?.contentGeneration ? {
      content_generation: row.workflowDetails.contentGeneration
    } : null;
    
    // Debug log to see what workflow data is available
    if (workflowData) {
      console.log('🔬 Workflow data available for prompt generation:', {
        hasContentGeneration: !!workflowData.content_generation
      });
    } else {
      console.log('📝 No workflow data available for this row');
    }
    
    const systemPromptTemplate = `You are a world-class SEO, AEO, and LLM SEO content marketer for US Citrus with deep expertise in creating comprehensive, AI-optimized articles that rank highly and get cited by AI answer engines (ChatGPT, Perplexity, Gemini, Claude, etc.). Your specialty is transforming content briefs into definitive resources that become the go-to sources for specific topics.

CRITICAL CONTENT PHILOSOPHY:
Your goal is to create content that becomes the definitive, comprehensive resource on the topic - the content that other creators reference and that AI engines cite as authoritative.

CONTENT COVERAGE REQUIREMENTS:
- Address ALL aspects of the topic comprehensively
- Include practical, actionable guidance that readers can implement
- Provide genuine value that advances knowledge in the space
- Cover both current best practices AND emerging trends
- Include specific examples, metrics, and concrete details

WRITING TONE AND STYLE (US Citrus AI Brand Voice - CRITICAL):

You write in the combined style of Gary Halbert (persuasive copywriter) and USCN Citrus Guide (expert educator). This is NON-NEGOTIABLE.

CORE APPROACH:
1. HOOK with Halbert benefits and urgency (subtle for blogs)
2. EDUCATE with USCN depth and authority
3. CLOSE with compelling call-to-action

GARY HALBERT ELEMENTS (VERY SUBTLE INTENSITY FOR BLOGS - 10% persuasion):
✅ Attention-grabbing headlines with clear benefits
✅ Conversational tone (use "you")
✅ Translate features to tangible benefits
✅ Create very subtle urgency (no aggressive FOMO)
✅ Short sentences for impact
✅ Proof and examples
✅ Repetition of key benefits (varied wording)

⚠️ CRITICAL FOR BLOGS: This is NOT a sales page. Focus on being helpful and building trust, not pushy.

USCN EXPERT ELEMENTS:
✅ Comprehensive, thorough coverage
✅ Step-by-step guidance
✅ Clear logical structure
✅ Anticipate follow-up questions
✅ "We've Been Where You Are" empathy
✅ Excellence positioning

CARE GUIDE PRIORITY RULES (HIGHEST PRIORITY):
⚠️ The US Citrus Nursery care guide is your PRIMARY source of truth
✅ Expand on care guide topics with more detail and examples (ALLOWED)
❌ NEVER contradict care guide facts (e.g., don't say pumice is IN Super Soil; it's an optional DIY amendment)
❌ NEVER introduce products or methods not in the care guide (e.g., NO neem oil)
❌ NEVER suggest synthetic fertilizers, chemical pesticides, or potting mix as acceptable

SUPER SOIL INGREDIENTS (EXACT FORMULA):
- 1/3 sand or sandy loam + 1/3 perlite OR rice hulls + 1/3 coco coir OR peat moss
- Plus: biochar, Crab Kelp & Amino Acids, sulfur (pH control), volcanic ash, microbials
- Pumice is NOT in Super Soil (it's an optional DIY amendment you COULD use)

⚠️ CRITICAL: When discussing Super Soil in content, you MUST mention that the complete Three Plant Pillars solution includes:
- Plant Super Boost (PSB) - live microbes (Pillar 1)
- Crab, Kelp & Amino Acids (CKAA) - organic fertilizer (Pillar 2)
- Super Soil (mineral-based soil - Pillar 3)
✅ CORRECT: "Dr. Mani's Magic Super Soil provides perfect drainage, optimal pH, and permanent structure. Complete the Three Plant Pillars with Crab, Kelp & Amino Acids for complete nutrition and Plant Super Boost for live microbes."
❌ WRONG: Only mentioning Super Soil without CKAA and PSB

WATERING INFORMATION (USE CARE GUIDE SCHEDULE):
⚠️ CRITICAL: Use the care guide watering schedule table (NOT generic "every 5-10 days"):
- < 60°F or indoors: Once per week
- 60-90°F, Humid: Twice weekly
- 60-90°F, Dry: Three times a week
- > 90°F, Humid: Every other day
- > 90°F, Dry: Daily
- Always use drench method: Water until water runs from the bottom drainage holes
- Adjust frequency based on wind, direct sun, pot size, leaf canopy, time of year, transplant status
❌ NEVER say: "Water every 5-10 days" or "Fertilize monthly during growing season" without the care guide schedule context

DIY SOIL MIXING INSTRUCTIONS (EXACT STEPS):
✅ MIXING STEPS:
1. Combine all dry ingredients in a large container
2. Mix thoroughly with hands or garden trowel (NOT garden fork)
3. Plant your tree, fill with soil up to where the top roots are flaring from the trunk and leave that uncovered
4. Add soil to the bottom of your pot so that your soil line of your tree will be ~2" below the soil container lip
5. Add mulch to the top. Our favorite is 1" of rice hulls
6. Drench your soil with water 1-2 times
7. Add Plant Super Boost and Crab, Kelp & Amino Acids

❌ NEVER include these steps:
- "Test pH and adjust if needed"
- "Moisten slightly before potting (should feel like a wrung-out sponge)"

❌ NEVER use "garden fork" - always use "garden trowel"

ESSENTIAL AMENDMENTS (DIY SOIL):
✅ Include: 1 cup biochar (for nutrient retention), 2 tablespoons sulfur (pH adjustment)
❌ DO NOT include volcanic ash or rock dust in DIY amendments
⚠️ Volcanic ash is part of Crab, Kelp & Amino Acids (CKAA) - it's already included when you use CKAA
🚫 NEVER say: "1/4 cup volcanic ash or rock dust (minerals)" in DIY instructions

APPROVED PEST CONTROL ONLY:
✅ Diatomaceous Earth, Micronized Sulfur, Castile/Horticultural Soap, Rubbing Alcohol, MalEx
❌ NEVER suggest neem oil, commercial pesticides, or products not in care guide

THREE PLANT PILLARS (UNIQUE TO USCN):
- Present as "US Citrus Nursery's Three Plant Pillars" or "USCN's proprietary framework"
- This is NOT common gardening knowledge - it's their unique system
- 1) Live Microbials, 2) Organic Fertilizer & Biostimulants, 3) Mineral-Based Soil

AI TELL REMOVAL RULES (CRITICAL):
🚫 NEVER use dashes or emdashes for emphasis or lists
✅ Use commas, periods, colons, semicolons, or bullet points instead
❌ WRONG: "mineral-based soil — never decomposes — provides oxygen"
✅ CORRECT: "mineral-based soil never decomposes and provides constant oxygen to roots"

AEO REQUIREMENTS (MUST PRESERVE WHILE APPLYING TONE):
⚠️ Apply tone WHILE maintaining all AEO best practices
- Direct answers in first paragraph under each heading
- Question-format headers matching search intent
- Proper semantic HTML hierarchy
- Tables for structured data
- Self-contained, extractable insights

INTENSITY FOR BLOGS: 90% education, 10% persuasion. Lead with educational value, close with soft CTA only.

COMPETITOR CONTEXT (MANDATORY):
- Target Keyword: must align with the user's target keyword
- Target Competitor URL: must be analyzed and explicitly outperformed in depth, clarity, and usefulness

BRAND CONTEXT (MUST INCORPORATE THROUGHOUT):
- Brand URL: {{ brand_kit.url }}
- About Brand: {{ brand_kit.about_brand }}
- Target Audience: {{ brand_kit.ideal_customer_profile }}
- Competitors: {{ brand_kit.competitors }}
- Brand POV: {{ brand_kit.brand_point_of_view }}
- Tone: {{ brand_kit.tone_of_voice }}
- Writing Rules: {{ brand_kit.writing_rules }}

AEO (ANSWER ENGINE OPTIMIZATION) PRINCIPLES:
- Structure for extractability with clear, self-contained insights
- Use semantic HTML and proper heading hierarchy (<h1> → <h2> → <h3>)
- Format data in proper <table> and <ul>/<ol> structures for easy AI parsing
- Include specific examples, metrics, and concrete details
- Write headlines that match search intent ("How to...", "What is...", "Best ways to...")
- Place the most important answer in the first paragraph under each heading

FORMATTING REQUIREMENTS:
1. **Proper HTML Structure:**
   - Use <h1> for main title, <h2> for major sections, <h3> for subsections
   - Format all lists with proper <ul>/<ol>, and <li> tags
   - Use <table> elements for any comparative data, features, or structured information
   - Include <p> tags for all paragraphs
   - Use <strong> for emphasis and key concepts

2. **Tables and Structured Data:**
   - When presenting comparisons, features, pricing, or any structured data, ALWAYS use HTML tables
   - Include proper <thead>, <tbody>, <th>, and <td> elements
   - Use tables for: feature comparisons, pricing tiers, pros/cons, statistics, timelines, etc.

3. **Brand Kit Variable Integration:**
   - MUST process and include brand kit variables naturally throughout content
   - Use {{ brand_kit.ideal_customer_profile }} for testimonials and customer examples
   - Include {{ brand_kit.competitors }} when discussing competitive landscape
   - Reference {{ brand_kit.brand_point_of_view }} in strategic sections
   - End with strong CTA using this exact anchor text: "${selectedCTA}"
   - Apply {{ brand_kit.tone_of_voice }} consistently throughout
   - Follow {{ brand_kit.writing_rules }} for style and approach

IMPORTANT: The current year is ${currentYear}. When referencing "current year," "this year," or discussing recent trends, always use ${currentYear}. Do not reference 2024 or earlier years as current.

=====================================
CTA REQUIREMENTS BY ARTICLE TYPE (MANDATORY)
=====================================

⚠️ CRITICAL: Different article types require different CTA structures at the end:

**FOR "HOW-TO CARE GUIDE" ARTICLES** (e.g., "How to Care for Lemon Trees", "How to Water Lemon Trees", "How to Fertilize Citrus Trees"):
MUST include TWO paragraphs before the final conclusion:

1. **Three Plant Pillars Product Links Paragraph:**
   - Mention the Three Plant Pillars framework
   - Link to where readers can get CKAA: <a href="https://uscitrusnursery.com/products/crab-kelp-amino-acids" target="_blank">Crab, Kelp & Amino Acids</a>
   - Link to where readers can get Plant Super Boost: <a href="https://uscitrusnursery.com/products/plant-super-boost" target="_blank">Plant Super Boost</a>
   - Link to where readers can get Super Soil: <a href="https://uscitrusnursery.com/products/dr-manis-magic-super-soil" target="_blank">Super Soil</a>
   - Format example: "Here are the Three Plant Pillars. This is where you get your <a href="https://uscitrusnursery.com/products/crab-kelp-amino-acids" target="_blank">Crab, Kelp & Amino Acids</a>. This is where you get your <a href="https://uscitrusnursery.com/products/plant-super-boost" target="_blank">Plant Super Boost</a>. This is where you get your <a href="https://uscitrusnursery.com/products/dr-manis-magic-super-soil" target="_blank">Super Soil</a>."

2. **Citrus Tree Collection Link Paragraph:**
   - Text: "Hey, are you interested in a lemon tree? Click here to get our lemon tree."
   - Link to: <a href="https://uscitrusnursery.com/collections/citrus-trees" target="_blank">https://uscitrusnursery.com/collections/citrus-trees</a>

**FOR GENERAL/INFORMATIONAL ARTICLES** (e.g., "What is a Lemon Tree?", "Where Do Lemon Trees Come From?", "Lemon Tree Varieties"):
MUST include ONE paragraph at the bottom before final conclusion:

- Message: "Hey, are you interested in growing your own lemons? You can grow your own lemons. Here's a guide on how to do it yourself. We also grow lemon trees and sell them to you. We can ship them to you if you live in any state except [check shipping restrictions - may need to specify states that cannot receive shipments]."
- Include link to care guide/resources: <a href="https://uscitrusnursery.com/pages/citrus-care-guide" target="_blank">citrus care guide</a>
- Include link to lemon tree collection: <a href="https://uscitrusnursery.com/collections/citrus-trees" target="_blank">our citrus trees</a>

**DETERMINING ARTICLE TYPE:**
- If keyword contains "how to", "how do", "care for", "watering", "fertilizing", "pruning" → How-To Care Guide
- If keyword contains "what is", "where", "varieties", "types", "origin", "history" → General/Informational

CRITICAL OUTPUT REQUIREMENTS:
- Return ONLY clean HTML content without any markdown code blocks, explanatory text, or meta-commentary
- DO NOT include phrases like "Here's the content:" or HTML code block markers
- Start directly with the HTML content and end with the closing HTML tag
- No markdown formatting, no code block indicators, no explanatory paragraphs

CONTENT STRUCTURE REQUIREMENTS:
1. **Compelling H1 Headline** (question format when appropriate)
2. **Authority-Establishing Introduction** (preview value and set expectations)
3. **Comprehensive Sections** with proper H2/H3 hierarchy
4. **Tables for Structured Data** (comparisons, features, statistics)
5. **Practical Implementation Guidance** with step-by-step processes
6. **Real-World Examples** and case studies (using brand kit data)
7. **Natural US Citrus Promotion** - End with compelling call-to-action using brand kit variables

BRAND INTEGRATION GUIDELINES:
- Lead with value and insights, not promotional content
- Use brand context to enhance credibility and expertise
- Include specific outcomes and metrics where relevant
- Position brand solutions naturally within comprehensive guidance
- Focus on helping readers achieve their goals first

Remember: Create the definitive resource that makes other content feel incomplete by comparison. Every section should provide genuine value and actionable insights.`;

    // For PAA content, we don't use deep research or gap analysis
    // The content generation is based directly on the FAQ answer from markdown files
    const researchSection = '';
    
    const productUrl2 = `https://uscitrusnursery.com/products/${row.treeSlug}`;
    const blogUrl2 = `https://uscitrusnursery.com/blogs/citrus-simplified/${row.treeSlug}`;
    const userPromptTemplate = `OBJECTIVE: Create comprehensive FAQ content answering "${row.question}" for ${row.treeName}

PRODUCT CONTEXT:
- Product URL: ${productUrl2}
- Blog URL: ${blogUrl2}

BRAND CONTEXT:
- About: {{ brand_kit.about_brand }}
- Target Audience: {{ brand_kit.ideal_customer_profile }}
- Brand POV: {{ brand_kit.brand_point_of_view }}
- Tone: {{ brand_kit.tone_of_voice }}
- Writing Rules: {{ brand_kit.writing_rules }}${researchSection}${workflowData ? `

⚠️ IMPORTANT RESEARCH INTEGRATION REQUIREMENTS:
────────────────────────────────────────
The above RESEARCH & ANALYSIS CONTEXT contains:
• Deep research insights from comprehensive analysis
• Identified content gaps from competitor analysis
• Strategic recommendations for content structure
• SEO optimization suggestions
• Market and audience insights

YOU MUST:
✓ Incorporate ALL deep research insights listed above
✓ Address ALL identified content gaps
✓ Follow the primary content angle recommendation
✓ Implement ALL structure recommendations
✓ Apply ALL SEO optimization suggestions
────────────────────────────────────────` : ''}

Create comprehensive AEO-optimized FAQ content for ${currentYear} that authoritatively answers the question above.

CRITICAL CONTENT REQUIREMENTS:

1. HTML Structure & Formatting:
   - Create an H1 title that directly addresses the keyword (use question format when appropriate)
   - Use proper heading hierarchy with H2 for major sections, H3 for subsections
   - Format ALL lists with proper <ul>/<ol> and <li> tags
   - Create HTML tables for ANY structured data (features, comparisons, statistics, timelines)
   - Use <p> tags for all paragraphs, <strong> for emphasis

2. Required Tables/Structured Data:
   - Include at least 2-3 HTML tables presenting relevant information such as:
     * Feature comparisons or capability matrices
     * Implementation timelines or process steps
     * Statistics or performance metrics
     * Pricing or value comparisons
     * Best practices checklist
   - Format tables with proper <thead>, <tbody>, <th>, and <td> elements

3. Brand Kit Variable Integration (MANDATORY):
   - Use {{ brand_kit.ideal_customer_profile }} to include customer testimonials or examples (at least once)
   - Reference {{ brand_kit.competitors }} when discussing market landscape
   - Apply {{ brand_kit.brand_point_of_view }} in strategic sections
   - Follow {{ brand_kit.tone_of_voice }} throughout the content
   - Implement {{ brand_kit.writing_rules }} for style consistency
   - End with the mandatory conclusion structure including CTA using this exact anchor text: "${selectedCTA}"

4. Content Depth & Value:
   - Provide comprehensive coverage that serves as the definitive resource
   - Include practical, actionable guidance with specific examples
   - Address both current best practices and emerging trends for ${currentYear}
   - Cover implementation strategies with step-by-step processes
   - Include relevant metrics, benchmarks, and data points${workflowData ? `
   
   📊 RESEARCH-DRIVEN REQUIREMENTS:
   - MANDATORY: Incorporate ALL deep research insights from the RESEARCH CONTEXT section
   - MANDATORY: Address ALL content gaps identified in the gap analysis above
   - MANDATORY: Follow the primary content angle specified in the research
   - MANDATORY: Implement ALL structure recommendations provided
   - MANDATORY: Include market and audience insights where relevant` : ''}

5. AEO Optimization:
   - Structure content for AI answer engine extraction
   - Use semantic HTML elements appropriately
   - Include self-contained insights that can be cited independently
   - Write clear, precise language that AI can easily understand
   - Format for both deep reading and quick reference

6. Technical Requirements:
   - Do NOT use emdashes (—) in the content
   - Avoid AI-detectable phrases like "It's not just about..., it's..." or "This doesn't just mean..., it also means..."
   - Include inline links to relevant external resources: <a href="URL" target="_blank">anchor text</a>

CRITICAL OUTPUT FORMAT: Respond with a JSON object containing exactly three fields:
{
  "content": "Complete HTML article with proper structure, tables, and brand kit variables processed",
  "seoTitle": "AEO-optimized title for AI search engines (<= 60 characters + ' | US Citrus')",
  "seoDescription": "Natural, value-focused description (150-160 chars) optimized for AI search extraction"
}

META FIELD REQUIREMENTS FOR AI SEARCH OPTIMIZATION:

seoTitle:
- MUST be a natural question format that includes the main keyword
- Choose format based on keyword type:
  * Job titles (singular): "Who Is A [Job Title]? [Descriptive Context]" (e.g., "Who Is An SDR Manager? Roles, Responsibilities, Salary")
  * Job titles (plural): "Who Are [Job Titles]? [Descriptive Context]" (e.g., "Who Are Sales Consultants? Roles, Skills, Career Path")
  * Processes/concepts: "What Is [Process]? [Descriptive Context]" (e.g., "What Is Sales Prospecting? Strategies, Tools, Best Practices")
  * Tools/software: "How Does [Tool] Work? [Descriptive Context]" (e.g., "How Does CRM Software Work? Features, Benefits, Implementation")
  * Strategies/methods: "Why Use [Strategy]? [Descriptive Context]" (e.g., "Why Use Account-Based Marketing? Benefits, Process, ROI")
- Maximum 70 characters INCLUDING " | US Citrus" (total <= 70 chars)
- MUST use proper Title Case (capitalize all major words)
- MUST include descriptive context beyond just the basic question
- Add relevant descriptive elements: roles, responsibilities, salary, benefits, strategies, tools, best practices, etc.
- The keyword should appear naturally and grammatically correctly
- Optimize for AI search engines with human-like, intelligent phrasing

seoDescription:
- MUST directly answer the title question using the main keyword naturally
- Adapt answer format to keyword type:
  * Job roles: "A [Job Title] is [role definition/who they are]. They [main responsibilities/activities]. US Citrus AI helps [job titles] [specific benefit]."
  * Processes: "[Process] is [definition]. It involves [key steps]. US Citrus AI provides [specific tools/features]."
  * Tools: "[Tool] helps [main function]. It [key capabilities]. US Citrus AI offers [specific advantage]."
- Exactly 150-160 characters
- Must be complete sentences ending with a period
- Write naturally with proper grammar and intelligent phrasing

INTELLIGENT QUESTION-ANSWER FORMAT EXAMPLES:

• Job Title (Singular): "Who Is An SDR Manager? Roles, Responsibilities, Salary | US Citrus AI"
  Description: "An SDR Manager is a sales leader who oversees development teams and prospecting strategies. They coach reps and optimize processes. US Citrus AI helps SDR Managers track team performance."

• Job Title (Plural): "Who Are Sales Consultants? Skills, Career Path, Salary | US Citrus AI"
  Description: "Sales Consultants are professionals who advise prospects on solutions for their business needs. They build relationships and close deals. US Citrus AI provides consultants with prospect intelligence."

• Process/Concept: "What Is Cold Email Marketing? Strategies, Tools, Best Practices | US Citrus AI"
  Description: "Cold email marketing is outreach to prospects without prior contact. It uses personalized messages to generate leads. US Citrus AI provides templates and automation tools."

• Strategy/Method: "Why Use Account-Based Marketing? Benefits, Process, ROI | US Citrus AI"
  Description: "Account-based marketing targets specific high-value accounts with personalized campaigns. It aligns sales and marketing teams. US Citrus AI enables ABM with contact data."

ABSOLUTELY FORBIDDEN IN META FIELDS:
- Grammatically incorrect questions ("What Is A Sales Consultants?", "What Are A Sales Consultant?", "What Is A Account Executives?")
- Including "| US Citrus AI" in H1 headlines (that's only for SEO titles, not content headlines)
- Robotic/boilerplate phrasing that doesn't sound human-written
- Rigid "What Is [keyword]?" format for all keyword types without considering singular/plural grammar
- Non-question titles ("Sales Prospecting Tips", "Lead Generation Methods")
- The word "Guide" or "Guides" (use intelligent question format instead)
- "Complete Guide" or "Comprehensive Guide" (use appropriate question type)
- "Ultimate Guide" (use natural question format)
- Descriptions that don't answer the title question directly
- Descriptions that don't include the main keyword naturally
- Marketing language like "game-changing", "revolutionary", "ultimate"
- Vague promises like "strategies", "frameworks", "playbooks" without specifics
- Generic terms like "everything you need to know"

CRITICAL: YOU MUST RETURN ONLY VALID JSON - NO OTHER TEXT ALLOWED
- Start response with { and end with }
- NO text before or after JSON
- NO markdown code blocks
- NO explanations like "Here is your JSON:"
- Put ALL HTML inside the content field as a properly escaped JSON string
- Escape ALL quotes with backslashes (\\" not ")
- Do NOT include literal newlines or unescaped quotes in JSON strings`;

    setSystemPrompt(systemPromptTemplate);
    setUserPrompt(userPromptTemplate);
  };

  useEffect(() => {
    if (!isOpen) return;
    setCopied(false);
    
    // Parse content to extract SEO fields if not already set
    // Why this matters: Extracts SEO metadata from content without overriding the content itself
    // Content is already set in the first useEffect using cleanAIContent directly
    const content = row.output || '';
    if (content.trim().length > 0 && (!seoTitle || !seoDescription)) {
      console.log('📊 Parsing content for SEO fields for question:', row.question);
      console.log('🔄 Content length:', content.length, 'characters');
      
      // Check if meta fields are already in row.metadata (from backend)
      const metadataHasMeta = row.metadata?.seoTitle || row.metadata?.seoDescription;
      
      // Parse the existing content to extract meta fields if present
      const parsed = parseAIResponse(content);
      
      // Set SEO fields from row metadata/properties first, then parsed content
      // Why this matters: We prioritize SEO fields from the backend
      const hasSeoTitle = (row.metadata?.seoTitle || row.seoTitle) && (row.metadata?.seoTitle || row.seoTitle || '').length > 0;
      const hasSeoDesc = (row.metadata?.seoDescription || row.seoDescription) && (row.metadata?.seoDescription || row.seoDescription || '').length > 0;
      
      if (hasSeoTitle) {
        const title = row.metadata?.seoTitle || row.seoTitle || '';
        console.log('✅ Using SEO title from row:', title);
        setSeoTitle(title);
      } else if (parsed.seoTitle) {
        console.log('✅ Using SEO title from parsed content:', parsed.seoTitle);
        setSeoTitle(parsed.seoTitle);
      } else {
        // Fallback: generate from tree name
        const fallbackTitle = `${row.treeName}: Frequently Asked Questions & Answers`;
        console.log('⚠️ Using fallback SEO title:', fallbackTitle);
        setSeoTitle(fallbackTitle);
      }
      
      if (hasSeoDesc) {
        const desc = row.metadata?.seoDescription || row.seoDescription || '';
        console.log('✅ Using SEO description from row:', desc);
        setSeoDescription(desc);
      } else if (parsed.seoDescription) {
        console.log('✅ Using SEO description from parsed content:', parsed.seoDescription);
        setSeoDescription(parsed.seoDescription);
      } else {
        // Fallback: generate from tree name
        const fallbackDesc = `Find answers to common questions about ${row.treeName}. Learn about care, growing tips, and more.`;
        console.log('⚠️ Using fallback SEO description:', fallbackDesc);
        setSeoDescription(fallbackDesc);
      }
    }
    
    // Load saved prompts; only generate defaults if none saved
    try {
      const saved = localStorage.getItem(`us_citrus_competitor_prompts_draft_${row.id}`);
      if (saved) {
        const { systemPrompt: savedSystem, userPrompt: savedUser } = JSON.parse(saved);
        const hasSaved = (typeof savedSystem === 'string' && savedSystem.length > 0) || (typeof savedUser === 'string' && savedUser.length > 0);
        if (hasSaved) {
          if (typeof savedSystem === 'string') setSystemPrompt(savedSystem);
          if (typeof savedUser === 'string') setUserPrompt(savedUser);
          return;
        }
      }
    } catch {}
    // No saved prompts → set defaults
    generateInitialPrompts();
  }, [isOpen, row.id, row.output]);

  // Add CSS for content formatting (same as BlogContentActionModal)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      
      .competitor-content-display {
        font-family: 'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        line-height: 1.6;
        color: #374151;
      }

      .content-modal-btn,
      button {
        font-family: 'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
      }

      .competitor-content-display h1 {
        font-family: 'FoundersGrotesk', Arial, sans-serif;
        font-size: 2rem;
        font-weight: 700;
        color: #111827;
        margin: 0 0 1.5rem 0;
        line-height: 1.2;
        border-bottom: 0.125rem solid #f3f4f6;
        padding-bottom: 0.75rem;
      }

      .competitor-content-display h2 {
        font-family: 'FoundersGrotesk', Arial, sans-serif;
        font-size: 1.5rem;
        font-weight: 600;
        color: #1f2937;
        margin: 2rem 0 1rem 0;
        line-height: 1.3;
      }

      .competitor-content-display h3 {
        font-family: 'FoundersGrotesk', Arial, sans-serif;
        font-size: 1.25rem;
        font-weight: 600;
        color: #374151;
        margin: 1.5rem 0 0.75rem 0;
        line-height: 1.4;
      }

      .competitor-content-display h4,
      .competitor-content-display h5,
      .competitor-content-display h6 {
        font-family: 'FoundersGrotesk', Arial, sans-serif;
        font-weight: 600;
        color: #374151;
        margin: 1rem 0 0.5rem 0;
        line-height: 1.4;
      }

      .competitor-content-display h4 {
        font-size: 1.125rem;
      }

      .competitor-content-display h5 {
        font-size: 1rem;
      }

      .competitor-content-display h6 {
        font-size: 0.875rem;
      }

      .competitor-content-display p {
        margin: 0 0 1.25rem 0;
        line-height: 1.7;
      }

      .competitor-content-display ul, 
      .competitor-content-display ol {
        margin: 1rem 0;
        padding-left: 1.5rem;
      }

      .competitor-content-display li {
        margin: 0.5rem 0;
        line-height: 1.6;
      }

      .competitor-content-display strong {
        font-weight: 600;
        color: #111827;
      }

      .competitor-content-display table {
        width: 100%;
        border-collapse: collapse;
        margin: 1.5rem 0;
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .competitor-content-display thead {
        background-color: #f9fafb;
      }

      .competitor-content-display th {
        padding: 0.875rem 1rem;
        text-align: left;
        font-weight: 600;
        color: #374151;
        border-bottom: 2px solid #e5e7eb;
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .competitor-content-display td {
        padding: 0.875rem 1rem;
        border-bottom: 1px solid #f3f4f6;
        color: #6b7280;
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .competitor-content-display tbody tr:hover {
        background-color: #f9fafb;
      }

      .competitor-content-display tbody tr:last-child td {
        border-bottom: none;
      }

      .competitor-content-display th:first-child,
      .competitor-content-display td:first-child {
        padding-left: 1.25rem;
      }

      .competitor-content-display th:last-child,
      .competitor-content-display td:last-child {
        padding-right: 1.25rem;
      }

      .competitor-content-display a {
        color: #2563eb;
        text-decoration: none;
      }

      .competitor-content-display a:hover {
        text-decoration: underline;
      }

      .competitor-content-display blockquote {
        border-left: 4px solid #e5e7eb;
        padding-left: 1rem;
        margin: 1.5rem 0;
        font-style: italic;
        color: #6b7280;
      }

      .competitor-content-display code {
        background-color: #f3f4f6;
        padding: 0.125rem 0.25rem;
        border-radius: 0.25rem;
        font-family: 'FoundersGroteskMono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 0.875em;
      }

      .competitor-content-display pre {
        background-color: #f8fafc;
        padding: 1rem;
        border-radius: 0.5rem;
        overflow-x: auto;
        margin: 1rem 0;
        border: 1px solid #e2e8f0;
      }

      .competitor-content-display pre code {
        background-color: transparent;
        padding: 0;
      }

      /* Image and figure styles for generated content */
      .competitor-content-display img {
        max-width: 100%;
        height: auto;
        display: block;
        margin: 1rem auto;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .competitor-content-display figure {
        margin: 2rem 0;
        text-align: center;
      }

      .competitor-content-display figure img {
        margin: 0 auto;
      }

      .competitor-content-display figcaption {
        margin-top: 0.75rem;
        font-size: 0.9rem;
        color: #666;
        font-style: italic;
        line-height: 1.4;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  /**
   * Extract Meta Title and Meta Description from content before cleaning
   * Why this matters: The backend may return these as plain text labels in the content
   */
  const extractMetaFieldsFromContent = (content: string): { metaTitle: string; seoDescription: string } => {
    const metaTitleMatch = content.match(/^\s*Meta\s+Title:\s*([^\n]+)/mi);
    const metaDescMatch = content.match(/^\s*Meta\s+Description:\s*([^\n]+)/mi);
    
    return {
      metaTitle: metaTitleMatch ? metaTitleMatch[1].trim() : '',
      seoDescription: metaDescMatch ? metaDescMatch[1].trim() : ''
    };
  };

  /**
   * cleanAIContent (copied from BlogContentActionModal)
   * Why this matters: Uses the exact same content cleaning logic as BlogContentActionModal for consistency
   */
  const cleanAIContent = (content: string): string => {
    let cleaned = content;
    
    // Remove markdown code block indicators (```json, """, etc.)
    cleaned = cleaned.replace(/^```json\s*/i, '');
    cleaned = cleaned.replace(/^```\s*/gm, '');
    cleaned = cleaned.replace(/\s*```\s*$/gm, '');
    cleaned = cleaned.replace(/^"""\s*json\s*/i, '');
    cleaned = cleaned.replace(/^"""\s*/gm, '');
    cleaned = cleaned.replace(/\s*"""\s*$/gm, '');
    
    // Remove literal JSON wrapper at the very start: { "content": "
    cleaned = cleaned.replace(/^\s*\{\s*"content"\s*:\s*"/i, '');
    
    // First pass: Remove any JSON wrapper artifacts at the very beginning
    cleaned = cleaned.replace(/^\s*```json\s*\n?/i, ''); // Remove opening ```json
    cleaned = cleaned.replace(/^\s*json\s*\n?/i, ''); // Remove standalone "json" at start
    cleaned = cleaned.replace(/^\s*\{\s*\n?\s*"content"\s*:\s*"\s*\n?/i, ''); // Remove { "content": "
    
    // Remove JSON metadata that might be mixed in with content
    cleaned = cleaned.replace(/\{\s*"seoTitle"\s*:\s*"[^"]*"\s*,[\s\S]*?\}/g, '');
    cleaned = cleaned.replace(/\{\s*"content"\s*:\s*"[\s\S]*?"seoTitle"[\s\S]*?\}/g, '');
    
    // Remove literal JSON structure that appears as text content
    cleaned = cleaned.replace(/^\s*\{\s*$/gm, '');
    cleaned = cleaned.replace(/^\s*"content":\s*"?\s*$/gm, '');
    cleaned = cleaned.replace(/^\s*\}\s*$/gm, '');
    cleaned = cleaned.replace(/^[\s\n]*\{[\s\n]*"content":\s*"[\s\n]*/i, '');
    cleaned = cleaned.replace(/[\s\n]*"\s*\}[\s\n]*$/i, '');
    
    // Handle the specific pattern: { "content": " at the start
    cleaned = cleaned.replace(/^\s*\{\s*\n?\s*"content":\s*"\s*\n?\s*/i, '');
    // Handle trailing patterns
    cleaned = cleaned.replace(/\s*"\s*,?\s*"seoTitle"[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\s*"\s*\}\s*$/i, '');
    
    // Remove additional JSON artifacts that can appear in output
    cleaned = cleaned.replace(/^\s*"?json\s*$/gmi, ''); // Remove standalone "json" lines
    cleaned = cleaned.replace(/^\s*```json\s*$/gmi, ''); // Remove ```json markers
    cleaned = cleaned.replace(/^\s*```\s*$/gmi, ''); // Remove standalone ``` markers
    cleaned = cleaned.replace(/^\s*"content":\s*"?\s*$/gmi, ''); // Remove standalone "content": lines
    cleaned = cleaned.replace(/^\s*\{\s*"content":\s*"?\s*$/gmi, ''); // Remove { "content": lines
    cleaned = cleaned.replace(/^\s*"?\s*$/gm, ''); // Remove lines with just quotes
    
    // Remove JSON field names that appear as standalone text
    cleaned = cleaned.replace(/^\s*"seoTitle"\s*:\s*/gmi, '');
    cleaned = cleaned.replace(/^\s*"seoDescription"\s*:\s*/gmi, '');
    cleaned = cleaned.replace(/^\s*"content"\s*:\s*/gmi, '');
    
    // Remove "Meta Title:" and "Meta Description:" labels from content
    // Why this matters: These should be displayed in dedicated sections, not in the main content
    cleaned = cleaned.replace(/^\s*Meta\s+Title:\s*[^\n]*\n?/gmi, '');
    cleaned = cleaned.replace(/^\s*Meta\s+Description:\s*[^\n]*\n?/gmi, '');
    
    // Clean up literal escape sequences that appear as text (\\n instead of actual newlines)
    cleaned = cleaned.replace(/^\s*\\n\\n\s*/g, '');
    cleaned = cleaned.replace(/\s*\\n\\n\s*$/g, '');
    cleaned = cleaned.replace(/\\n\\n/g, '\n\n');
    cleaned = cleaned.replace(/\\n/g, '\n');
    cleaned = cleaned.replace(/\\t/g, '\t');
    cleaned = cleaned.replace(/\\"/g, '"');
    
    // Reduce excessive bold formatting - keep only strategic emphasis
    // Remove bold from entire sentences (more than 8 words)
    cleaned = cleaned.replace(/<strong>([^<]{60,}?)<\/strong>/g, '$1');
    // Remove bold from percentage/number + text combinations that are too long
    cleaned = cleaned.replace(/<strong>(\d+%?\s+[^<]{20,}?)<\/strong>/g, '<strong>$1</strong>'.replace(/<strong>(\d+%?\s+\w+\s+\w+)\s+([^<]+)<\/strong>/, '<strong>$1</strong> $2'));
    // Keep bold only for short key terms (under 4 words)
    cleaned = cleaned.replace(/<strong>([^<]*?\s+[^<]*?\s+[^<]*?\s+[^<]*?\s+[^<]+?)<\/strong>/g, (match, content) => {
      const words = content.trim().split(/\s+/);
      if (words.length <= 3) return match; // Keep short phrases
      if (/^\d+%/.test(content.trim())) return match; // Keep percentages
      return content; // Remove bold from longer phrases
    });
    
    // Remove common AI introductory phrases
    cleaned = cleaned.replace(/^.*?Here's the.*?content.*?:?\s*/i, '');
    cleaned = cleaned.replace(/^.*?Here's an.*?optimized.*?:?\s*/i, '');
    cleaned = cleaned.replace(/^.*?I'll create.*?:?\s*/i, '');
    cleaned = cleaned.replace(/^.*?Based on.*?analysis.*?:?\s*/i, '');
    
    // Remove markdown code blocks
    cleaned = cleaned.replace(/```html\s*/gi, '');
    cleaned = cleaned.replace(/```\s*/g, '');
    
    // Convert markdown headers to HTML headers (robust: allow leading spaces, all levels)
    // Why this matters: AI sometimes returns markdown instead of HTML despite instructions
    cleaned = cleaned.replace(/^\s*(#{1,6})\s+(.+)$/gm, (_m, hashes: string, text: string) => {
      const level = Math.min(Math.max(hashes.length, 1), 6);
      return `<h${level}>${text.trim()}</h${level}>`;
    });
    
    // Convert markdown bold and italic to HTML
    cleaned = cleaned.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    cleaned = cleaned.replace(/\*(.+?)\*/g, '<em>$1</em>');
    
    // Convert markdown links to HTML anchors (open in new tab)
    cleaned = cleaned.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');

    // Remove dashes and emdashes used for emphasis or separation (AI tells)
    // Why this matters: Ron identified dashes as an AI tell that makes content feel robotic
    // Replace emdashes (—) used for emphasis with commas or periods
    cleaned = cleaned.replace(/\s+—\s+/g, ', '); // "soil — never decomposes" → "soil, never decomposes"
    // Replace double hyphens (--) used as emdashes
    cleaned = cleaned.replace(/\s+--\s+/g, ', '); // "soil -- never decomposes" → "soil, never decomposes"
    // Clean up any resulting double commas
    cleaned = cleaned.replace(/,\s*,/g, ',');

    // Remove markdown checkbox syntax [ ] from content (AI tell)
    // Why this matters: Markdown checkboxes are an AI tell that don't render properly in HTML
    // Replace [ ] or [x] at the start of lines (common in checklists)
    cleaned = cleaned.replace(/^\s*\[\s*\]\s*/gm, ''); // Remove [ ] at start of lines
    cleaned = cleaned.replace(/^\s*\[\s*x\s*\]\s*/gmi, ''); // Remove [x] at start of lines
    // Also remove them inline if they appear mid-text
    cleaned = cleaned.replace(/\[\s*\]\s+/g, ''); // Remove [ ] followed by space
    cleaned = cleaned.replace(/\[\s*x\s*\]\s+/gi, ''); // Remove [x] followed by space

    // Convert inline code to <code>
    cleaned = cleaned.replace(/`([^`]+)`/g, '<code>$1</code>');

    // Convert blockquotes
    cleaned = cleaned.replace(/^>\s?(.*)$/gm, '<blockquote>$1</blockquote>');

    // Convert markdown lists to HTML lists
    // Handle unordered lists (- or *)
    cleaned = cleaned.replace(/(?:^|\n)([*-]\s+.+(?:\n[*-]\s+.+)*)/gm, (match) => {
      const items = match.trim().split('\n').map(line => {
        const item = line.replace(/^[*-]\s+/, '').trim();
        return `  <li>${item}</li>`;
      }).join('\n');
      return `<ul>\n${items}\n</ul>`;
    });
    
    // Handle ordered lists (1. 2. etc.)
    cleaned = cleaned.replace(/(?:^|\n)(\d+\.\s+.+(?:\n\d+\.\s+.+)*)/gm, (match) => {
      const items = match.trim().split('\n').map(line => {
        const item = line.replace(/^\d+\.\s+/, '').trim();
        return `  <li>${item}</li>`;
      }).join('\n');
      return `<ol>\n${items}\n</ol>`;
    });

    // Convert markdown tables (pipe syntax) to HTML tables
    // Why this matters: Ensures tables render properly instead of raw pipes
    const lines = cleaned.split(/\n/);
    const htmlLines: string[] = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const headerMatch = /^\s*\|(.+)\|\s*$/.exec(line || '');
      const sepLine = lines[i + 1] || '';
      const isSeparator = /^\s*\|?\s*(:?-{3,}:?\s*\|\s*)+(:?-{3,}:?\s*)\|?\s*$/.test(sepLine);
      if (headerMatch && isSeparator) {
        // Parse header row
        const headers = headerMatch[1].split('|').map(h => h.trim());
        i += 1; // skip separator row
        const bodyRows: string[][] = [];
        let j = i + 1;
        while (j < lines.length) {
          const rowLine = lines[j];
          if (!/^\s*\|(.+)\|\s*$/.test(rowLine)) break;
          const cells = rowLine.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim());
          bodyRows.push(cells);
          j++;
        }
        // Build HTML table
        const thead = `<thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>`;
        const tbody = `<tbody>${bodyRows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody>`;
        htmlLines.push(`<table>${thead}${tbody}</table>`);
        i = j - 1; // advance pointer to last processed row
        continue;
      }
      htmlLines.push(line);
    }
    cleaned = htmlLines.join('\n');
    
    // Convert remaining plain text paragraphs to HTML paragraphs
    // Split by double newlines and wrap non-HTML content in <p> tags
    const paragraphBlocks = cleaned.split(/\n\s*\n/);
    cleaned = paragraphBlocks.map(line => {
      const trimmed = line.trim();
      if (!trimmed) return '';
      
      // Skip if already HTML (contains < and >)
      if (trimmed.includes('<') && trimmed.includes('>')) {
        return trimmed;
      }
      
      // Skip if it's just a single word or very short
      if (trimmed.length < 3) return trimmed;
      
      // Wrap in paragraph tags
      return `<p>${trimmed}</p>`;
    }).join('\n\n');

    // Secondary pass: for any standalone lines still not wrapped and not HTML, wrap as paragraphs
    cleaned = cleaned.split(/\n/).map((line) => {
      const t = line.trim();
      if (!t) return '';
      const isHtml = /^<\/?(h\d|p|ul|ol|li|table|thead|tbody|tr|th|td|blockquote|pre|code|hr|div|span|a)\b/i.test(t);
      if (isHtml) return line;
      return `<p>${t}</p>`;
    }).join('\n');

    // Convert headings that slipped into <p> tags (e.g., <p># Title</p>)
    cleaned = cleaned.replace(/<p>\s*(#{1,6})\s+([^<]+)<\/p>/g, (_m, hashes: string, text: string) => {
      const level = Math.min(Math.max(hashes.length, 1), 6);
      return `<h${level}>${text.trim()}</h${level}>`;
    });
    
    // Remove explanatory text at the end (typically starts with patterns like "This content structure:")
    cleaned = cleaned.replace(/\n\s*This content structure:[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\n\s*Would you like me to[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\n\s*The content includes:[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\n\s*Key features of this content:[\s\S]*$/i, '');
    
    // Remove numbered analysis points at the end
    cleaned = cleaned.replace(/\n\s*\d+\.\s+[A-Z][^<\n]*[\s\S]*$/i, '');
    
    // Format email templates better - add line breaks after sentences in template sections
    cleaned = cleaned.replace(/(Template \d+:.*?<\/p>|<p>.*?Template.*?<\/p>)/gi, (match) => {
      // Add line breaks after periods followed by space and capital letter (sentence boundaries)
      return match.replace(/\.\s+([A-Z])/g, '.<br><br>$1');
    });
    
    // Add line breaks after common email elements
    cleaned = cleaned.replace(/(Subject:.*?)([A-Z][a-z])/g, '$1<br><br>$2');
    cleaned = cleaned.replace(/(Hi \{\{.*?\}\},)\s*([A-Z])/g, '$1<br><br>$2');
    cleaned = cleaned.replace(/(Best regards,|Best,|Sincerely,)\s*([A-Z\[])/g, '$1<br><br>$2');
    
    // If content starts with HTML, ensure it starts with a proper tag
    if (cleaned.includes('<') && !cleaned.trim().startsWith('<')) {
      const htmlStart = cleaned.indexOf('<');
      if (htmlStart > 0) {
        cleaned = cleaned.substring(htmlStart);
      }
    }
    
    // Trim whitespace and clean up extra newlines
    cleaned = cleaned.trim().replace(/\n{3,}/g, '\n\n');

    // Final safety: convert any remaining markdown headers and strip stray bold markers
    cleaned = cleaned.replace(/^\s*(#{1,6})\s+(.+)$/gm, (_m, hashes: string, text: string) => {
      const level = Math.min(Math.max(hashes.length, 1), 6);
      return `<h${level}>${text.trim()}</h${level}>`;
    });
    // Remove stray leading '#' characters that survived (e.g., malformed headings)
    cleaned = cleaned.replace(/<p>\s*#+\s*([^<]+)<\/p>/g, '<p>$1</p>');
    cleaned = cleaned.replace(/(^|\n)\s*#+\s*(.+)(?=\n|$)/g, (_m, prefix: string, text: string) => `${prefix}<h2>${text.trim()}</h2>`);

    // Remove '#' prefixes that appear inside already-HTML headings like <h2># Title</h2>
    cleaned = cleaned.replace(/<(h[1-6])>\s*#+\s*([^<]+)<\/\1>/gi, '<$1>$2</$1>');
    // And inside paragraphs just in case
    cleaned = cleaned.replace(/<p>\s*#+\s*([^<]+)<\/p>/gi, '<p>$1</p>');
    // Remove any leftover unmatched ** that weren't part of a valid pair
    cleaned = cleaned.replace(/\*\*/g, '');
    
    // Final pass: Remove any remaining JSON artifacts that might have survived
    cleaned = cleaned.replace(/^\s*"?\s*$/gm, ''); // Remove lines with just quotes or whitespace
    cleaned = cleaned.replace(/^\s*json\s*$/gmi, ''); // Remove any remaining "json" lines
    cleaned = cleaned.replace(/^\s*"content"\s*:\s*"?\s*$/gmi, ''); // Remove any remaining "content": lines
    cleaned = cleaned.replace(/^\s*\{\s*$/gm, ''); // Remove standalone opening braces
    cleaned = cleaned.replace(/^\s*\}\s*$/gm, ''); // Remove standalone closing braces
    cleaned = cleaned.replace(/^\s*```\s*$/gm, ''); // Remove any remaining code block markers
    
    // Remove empty lines created by the cleaning process
    cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n'); // Reduce multiple empty lines to double
    cleaned = cleaned.trim(); // Remove leading/trailing whitespace
    
    return cleaned;
  };

  /**
   * processLiquidVariables
   * Why this matters: Replaces {{ brand_kit.* }} tokens before sending prompts to backend.
   */
  const processLiquidVariables = (text: string, kit: BrandKit | null): string => {
    if (!kit) return text;
    let processed = text;

    // Standard variables
    processed = processed.replace(/\{\{\s*brand_kit\.url\s*\}\}/g, kit.url || '');
    processed = processed.replace(/\{\{\s*brand_kit\.about_brand\s*\}\}/g, kit.aboutBrand || '');
    processed = processed.replace(/\{\{\s*brand_kit\.ideal_customer_profile\s*\}\}/g, kit.idealCustomerProfile || '');
    processed = processed.replace(/\{\{\s*brand_kit\.competitors\s*\}\}/g, kit.competitors || '');
    processed = processed.replace(/\{\{\s*brand_kit\.brand_point_of_view\s*\}\}/g, kit.brandPointOfView || '');
    processed = processed.replace(/\{\{\s*brand_kit\.author_persona\s*\}\}/g, kit.authorPersona || '');
    processed = processed.replace(/\{\{\s*brand_kit\.tone_of_voice\s*\}\}/g, kit.toneOfVoice || '');
    processed = processed.replace(/\{\{\s*brand_kit\.header_case_type\s*\}\}/g, kit.headerCaseType || '');
    processed = processed.replace(/\{\{\s*brand_kit\.writing_rules\s*\}\}/g, kit.writingRules || '');
    // Use random CTA instead of brand kit CTA to maintain even distribution
    processed = processed.replace(/\{\{\s*brand_kit\.cta_text\s*\}\}/g, getRandomCTAAnchorText());
    processed = processed.replace(/\{\{\s*brand_kit\.cta_destination\s*\}\}/g, kit.ctaDestination || '');

    // Competitor-conquesting extras (optional; empty by default)
    processed = processed.replace(/\{\{\s*brand_kit\.research_key_insights\s*\}\}/g, (kit as any)?.research_key_insights || '');
    processed = processed.replace(/\{\{\s*brand_kit\.gap_analysis_summary\s*\}\}/g, (kit as any)?.gap_analysis_summary || '');
    processed = processed.replace(/\{\{\s*brand_kit\.primary_angle\s*\}\}/g, (kit as any)?.primary_angle || '');
    processed = processed.replace(/\{\{\s*brand_kit\.structure_recommendations\s*\}\}/g, (kit as any)?.structure_recommendations || '');
    processed = processed.replace(/\{\{\s*brand_kit\.seo_suggestions\s*\}\}/g, (kit as any)?.seo_suggestions || '');

    // Custom variables
    if (kit.customVariables) {
      Object.keys(kit.customVariables).forEach((key) => {
        const regex = new RegExp(`\\{\\{\\s*brand_kit\\.${key}\\s*\\}}`, 'g');
        processed = processed.replace(regex, kit.customVariables?.[key] || '');
      });
    }
    return processed;
  };

  /**
   * handleCopy
   * Why this matters: Quick copy for moving generated content elsewhere.
   */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(isEditingContent ? editableContent : generatedContent || row.output || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  /**
   * handleReset
   * Why this matters: Restores reliable defaults after experimentation.
   */
  const handleReset = () => {
    generateInitialPrompts();
  };

  /**
   * openInHTML
   * Why this matters: Mirrors Blog modal to preview raw HTML in a new tab for debugging and copy.
   */
  const openInHTML = () => {
    const contentToOpen = isEditingContent ? editableContent : generatedContent || row.output || '';
    if (!contentToOpen) {
      alert('No content to display. Please generate content first.');
      return;
    }

    // Create a simple HTML page showing the raw HTML source
    const rawHtmlDisplay = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Raw HTML Source - ${seoTitle || `${row.question} Content`}</title>
    <style>
        body {
            font-family: 'FoundersGroteskMono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            line-height: 1.6;
            margin: 0;
            padding: 20px;
            background-color: #1e1e1e;
            color: #d4d4d4;
        }
        
        .header {
            background-color: #2d2d30;
            padding: 20px;
            margin: -20px -20px 20px -20px;
            border-bottom: 3px solid #007acc;
        }
        
        .header h1 {
            color: #4ec9b0;
            margin: 0 0 10px 0;
            font-size: 24px;
        }
        
        .header p {
            color: #9cdcfe;
            margin: 5px 0;
            font-size: 14px;
        }
        
        .meta-info {
            background-color: #252526;
            padding: 15px;
            border-radius: 5px;
            margin-bottom: 20px;
            border-left: 4px solid #f59e0b;
        }
        
        .meta-info h3 {
            color: #f59e0b;
            margin: 0 0 10px 0;
            font-size: 16px;
        }
        
        .meta-field {
            margin: 8px 0;
            color: #d4d4d4;
            font-size: 13px;
        }
        
        .meta-label {
            color: #4fc1ff;
            font-weight: bold;
        }
        
        .code-container {
            background-color: #1e1e1e;
            border: 1px solid #3e3e42;
            border-radius: 5px;
            position: relative;
        }
        
        .code-header {
            background-color: #2d2d30;
            padding: 10px 15px;
            border-bottom: 1px solid #3e3e42;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .code-title {
            color: #cccccc;
            font-weight: bold;
            font-size: 14px;
        }
        
        .copy-btn {
            background-color: #0e639c;
            color: white;
            border: none;
            padding: 6px 12px;
            border-radius: 3px;
            cursor: pointer;
            font-size: 12px;
            font-family: inherit;
        }
        
        .copy-btn:hover {
            background-color: #1177bb;
        }
        
        .code-content {
            padding: 20px;
            overflow-x: auto;
            white-space: pre-wrap;
            word-wrap: break-word;
            max-height: 70vh;
            overflow-y: auto;
        }
        
        .html-code {
            color: #d4d4d4;
            font-size: 13px;
            line-height: 1.5;
        }
        
        /* HTML syntax highlighting */
        .html-tag { color: #569cd6; }
        .html-attr { color: #92c5f7; }
        .html-value { color: #ce9178; }
        .html-text { color: #d4d4d4; }
    </style>
</head>
<body>
    <div class="header">
        <h1>🔍 Raw HTML Source</h1>
        <p><strong>Question:</strong> ${row.question}</p>
        <p><strong>Tree:</strong> ${row.treeName}</p>
        <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
        <p><strong>Word Count:</strong> ~${Math.round(contentToOpen.replace(/<[^>]*>/g, '').split(' ').length)} words</p>
    </div>
    
    ${seoTitle || seoDescription ? `
    <div class="meta-info">
        <h3>📊 SEO Metadata</h3>
        ${seoTitle ? `<div class="meta-field"><span class="meta-label">Title:</span> ${seoTitle}</div>` : ''}
        ${seoDescription ? `<div class="meta-field"><span class="meta-label">Description:</span> ${seoDescription}</div>` : ''}
    </div>
    ` : ''}
    
    <div class="code-container">
        <div class="code-header">
            <span class="code-title">HTML Source Code</span>
            <button class="copy-btn" onclick="copyHtmlCode()">📋 Copy HTML</button>
        </div>
        <div class="code-content">
            <pre class="html-code" id="htmlCode">${contentToOpen.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
        </div>
    </div>

    <script>
        function copyHtmlCode() {
            const htmlCode = \`${contentToOpen.replace(/`/g, '\\`').replace(/\$/g, '\\$')}\`;
            navigator.clipboard.writeText(htmlCode).then(() => {
                const btn = document.querySelector('.copy-btn');
                const originalText = btn.textContent;
                btn.textContent = '✅ Copied!';
                btn.style.backgroundColor = '#10b981';
                setTimeout(() => {
                    btn.textContent = originalText;
                    btn.style.backgroundColor = '#0e639c';
                }, 2000);
            }).catch(err => {
                console.error('Failed to copy: ', err);
                alert('Failed to copy HTML. Please select and copy manually.');
            });
        }
    </script>
</body>
</html>`;

    // Open in new window
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(rawHtmlDisplay);
      newWindow.document.close();
    } else {
      alert('Popup blocked. Please allow popups and try again.');
    }
  };

  /**
   * toggleEditMode (updated to match BlogContentActionModal)
   * Why this matters: Enables inline edits before publishing or copying, with proper HTML formatting.
   */
  const toggleEditMode = () => {
    if (isEditingContent) {
      console.log('💾 [toggleEditMode] Saving content from edit mode...');
      console.log('📝 [toggleEditMode] Raw editable content length:', editableContent.length);
      
      // Compact the HTML for saving (CTAs are already styled, no conversion needed)
      const compactedContent = compactHTMLForSaving(editableContent);
      console.log('🗜️ [toggleEditMode] Compacted content length:', compactedContent.length);
      console.log('📋 [toggleEditMode] Final content preview (first 200 chars):', compactedContent.substring(0, 200));
      
      setGeneratedContent(compactedContent);
      console.log('✅ [toggleEditMode] Generated content updated');
      
      // Update the original row if callback provided
      if (onContentUpdate) {
        onContentUpdate(row.id, compactedContent);
      }
    } else {
      console.log('✏️ [toggleEditMode] Entering edit mode...');
      // Enter edit mode - sync editable content with current content and format for readability
      const contentToEdit = generatedContent || row.output || '';
      const formattedContent = formatHTMLForEditing(contentToEdit);
      setEditableContent(formattedContent);
      console.log('📝 [toggleEditMode] Editable content set, length:', formattedContent.length);
      // Initialize edit history for undo/redo functionality
      initializeEditHistory(formattedContent);
    }
    setIsEditingContent(!isEditingContent);
    console.log('🔄 [toggleEditMode] Edit mode toggled to:', !isEditingContent);
  };

  /**
   * copy helpers for content & meta fields
   * Why this matters: Provide identical feedback UX to Blog modal.
   */
  const [showCopiedMessage, setShowCopiedMessage] = useState<boolean>(false);
  const copyToClipboard = async () => {
    const contentToCopy = isEditingContent ? editableContent : generatedContent || row.output || '';
    try {
      await navigator.clipboard.writeText(contentToCopy);
      setShowCopiedMessage(true);
      setTimeout(() => setShowCopiedMessage(false), 2000);
    } catch (err) {
      console.error('Failed to copy content:', err);
      alert('Failed to copy content. Please select and copy manually.');
    }
  };
  const copySeoTitle = async () => {
    try { await navigator.clipboard.writeText(seoTitle); setShowSeoTitleCopied(true); setTimeout(() => setShowSeoTitleCopied(false), 2000);} catch {}
  };
  const copySeoDescription = async () => {
    try { await navigator.clipboard.writeText(seoDescription); setShowSeoDescCopied(true); setTimeout(() => setShowSeoDescCopied(false), 2000);} catch {}
  };

  /**
   * extractFieldsFromMalformedJSON
   * Why this matters: Robustly recover fields when the model returns almost-JSON.
   */
  const extractFieldsFromMalformedJSON = (responseText: string): { content: string; seoTitle: string; seoDescription: string } => {
    let content = '';
    let seoTitle = '';
    let seoDescription = '';
    try {
      const contentMatch = responseText.match(/"content"\s*:\s*"([\s\S]*?)"\s*,\s*"(?:seoTitle|seoDescription)"/);
      if (contentMatch) {
        content = contentMatch[1]
          .replace(/\\"/g, '"')
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t')
          .replace(/\\\\/g, '\\')
          // Clean up literal \n\n that appears as text
          .replace(/^\s*\\n\\n\s*/, '')
          .replace(/\s*\\n\\n\s*$/, '')
          .trim();
      }
      const titleMatch = responseText.match(/"seoTitle"\s*:\s*"([^\"]*?)"/);
      if (titleMatch) seoTitle = titleMatch[1];
      const descMatch = responseText.match(/"seoDescription"\s*:\s*"([^\"]*?)"/);
      if (descMatch) seoDescription = descMatch[1];
    } catch {}
    return { content, seoTitle, seoDescription };
  };



  /**
   * parseAIResponse (copied from BlogContentActionModal)
   * Why this matters: Uses the exact same parsing logic as BlogContentActionModal for consistency
   */
  const parseAIResponse = (responseText: string): { content: string; seoTitle: string; seoDescription: string } => {
    // Add detailed logging for debugging
    console.log('🔍 Raw AI Response Length:', responseText.length);
    console.log('🔍 Raw AI Response Preview:', responseText.substring(0, 200) + '...');
    console.log('🔍 Raw AI Response End:', '...' + responseText.substring(responseText.length - 200));
    
    // Check if this is legacy markdown content (not JSON)
    const isLegacyMarkdown = responseText.trim().startsWith('#') || 
                             responseText.trim().startsWith('##') ||
                             (responseText.includes('# ') && !responseText.trim().startsWith('{'));
    
    if (isLegacyMarkdown) {
      console.log('📜 Detected legacy markdown content, converting to expected format');
      return {
        content: cleanAIContent(responseText),
        seoTitle: generateFallbackTitle(row.question),
        seoDescription: generateFallbackDescription(row.question)
      };
    }
    
    try {
      // Clean the response text first
      let cleanedResponse = responseText.trim();
      
      // Remove any markdown code blocks that might be wrapping the JSON
      cleanedResponse = cleanedResponse.replace(/^```json\s*/i, '');
      cleanedResponse = cleanedResponse.replace(/\s*```$/i, '');
      cleanedResponse = cleanedResponse.replace(/^```\s*/i, '');
      
      console.log('🔍 Cleaned Response Length:', cleanedResponse.length);
      console.log('🔍 Cleaned Response Preview:', cleanedResponse.substring(0, 200) + '...');
      
      // First, try to parse the entire response as JSON
      let parsed;
      try {
        parsed = JSON.parse(cleanedResponse);
        console.log('✅ Successfully parsed entire response as JSON');
      } catch (parseError) {
        console.log('❌ Failed to parse entire response as JSON:', parseError);
        
        // Try to find JSON object boundaries more carefully
        let jsonStart = cleanedResponse.indexOf('{');
        let jsonEnd = cleanedResponse.lastIndexOf('}');
        
        if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
          const potentialJson = cleanedResponse.substring(jsonStart, jsonEnd + 1);
          console.log('🔍 Extracted potential JSON:', potentialJson.substring(0, 200) + '...');
          
          try {
            parsed = JSON.parse(potentialJson);
            console.log('✅ Successfully parsed extracted JSON');
          } catch (extractError) {
            console.log('❌ Failed to parse extracted JSON:', extractError);
            
            // Try more aggressive pattern matching
            const jsonMatches = cleanedResponse.match(/\{[^{}]*"content"[^{}]*"seoTitle"[^{}]*"seoDescription"[^{}]*\}/g) ||
                               cleanedResponse.match(/\{[^{}]*"seoTitle"[^{}]*"seoDescription"[^{}]*"content"[^{}]*\}/g) ||
                               cleanedResponse.match(/\{[\s\S]*?\}/g);
            
            console.log('🔍 Pattern matches found:', jsonMatches?.length || 0);
            
            if (jsonMatches && jsonMatches.length > 0) {
              // Try the longest match first (most likely to be complete)
              const sortedMatches = jsonMatches.sort((a, b) => b.length - a.length);
              
              for (let i = 0; i < sortedMatches.length; i++) {
                const match = sortedMatches[i];
                console.log(`🔍 Trying match ${i + 1}:`, match.substring(0, 100) + '...');
                
                try {
                  parsed = JSON.parse(match);
                  if (parsed && typeof parsed === 'object' && (parsed.content || parsed.seoTitle || parsed.seoDescription)) {
                    console.log('✅ Successfully parsed match', i + 1);
                    break;
                  }
                } catch (matchError) {
                  console.log(`❌ Failed to parse match ${i + 1}:`, matchError);
                  
                  // Try individual field extraction as final fallback
                  const extractedFields = extractFieldsFromMalformedJSON(cleanedResponse);
                  if (extractedFields.content || extractedFields.seoTitle || extractedFields.seoDescription) {
                    console.log('✅ Successfully extracted fields from malformed JSON');
                    parsed = extractedFields;
                    break;
                  }
                  continue;
                }
              }
            }
          }
        }
      }

      // Check if we have the expected JSON structure
      if (parsed && typeof parsed === 'object') {
        const hasContent = parsed.content && typeof parsed.content === 'string';
        const hasTitle = parsed.seoTitle && typeof parsed.seoTitle === 'string';
        const hasDescription = parsed.seoDescription && typeof parsed.seoDescription === 'string';
        
        console.log('🔍 Parsed object structure:', {
          hasContent,
          hasTitle,
          hasDescription,
          keys: Object.keys(parsed)
        });
        
        if (hasContent || hasTitle || hasDescription) {
          console.log('✅ Successfully parsed JSON response:', {
            hasContent,
            hasTitle,
            hasDescription,
            contentLength: hasContent ? parsed.content.length : 0,
            titleLength: hasTitle ? parsed.seoTitle.length : 0,
            descLength: hasDescription ? parsed.seoDescription.length : 0
          });
          
          // Clean up the parsed content to remove literal escape sequences
          let cleanedContent = hasContent ? parsed.content : responseText;
          if (hasContent) {
            // Remove literal \n\n sequences that appear as text
            cleanedContent = cleanedContent
              .replace(/^\s*\\n\\n\s*/, '')
              .replace(/\s*\\n\\n\s*$/, '')
              .replace(/\\n\\n/g, '\n\n')
              .replace(/\\n/g, '\n')
              .replace(/\\t/g, '\t')
              .replace(/\\"/g, '"')
              .trim();
          }
          
          return {
            content: cleanAIContent(cleanedContent),
            seoTitle: hasTitle ? parsed.seoTitle : generateFallbackTitle(row.question), // Use fallback if no AI title
            seoDescription: hasDescription ? parsed.seoDescription : generateFallbackDescription(row.question) // Use fallback if no AI description
          };
        } else {
          console.log('❌ Parsed object missing required fields');
        }
      } else {
        console.log('❌ Failed to get valid parsed object');
      }
    } catch (error) {
      console.log('❌ Unexpected error in parseAIResponse:', error);
    }

    console.log('⚠️ Falling back to legacy content parsing');
    
    // Try to extract meta fields from the raw text if they exist
    let extractedTitle = '';
    let extractedDescription = '';
    
    // Look for patterns like "seoTitle": "..."
    const titleMatch = responseText.match(/"seoTitle"\s*:\s*"([^"]+)"/);
    const descMatch = responseText.match(/"seoDescription"\s*:\s*"([^"]+)"/);
    
    if (titleMatch) {
      extractedTitle = titleMatch[1]; // Let AI generate proper length titles
      console.log('🔍 Extracted title via regex:', extractedTitle);
    }
    
    if (descMatch) {
      extractedDescription = descMatch[1];
      console.log('🔍 Extracted description via regex:', extractedDescription);
    }
    
    // Fallback to legacy content cleaning
    const cleanedContent = cleanAIContent(responseText);
    console.log('🔍 Cleaned content length:', cleanedContent.length);
    
    return {
      content: cleanedContent,
      seoTitle: extractedTitle || generateFallbackTitle(row.question),
      seoDescription: extractedDescription || generateFallbackDescription(row.question)
    };
  };

  /**
   * Google Docs / Google Sheets actions
   * Why this matters: Matches export actions in Blog modal for parity.
   */
  const openGoogleDocs = async () => {
    const contentToCopy = isEditingContent ? editableContent : generatedContent || row.output || '';
    if (!contentToCopy) {
      alert('Please generate content first before creating a Google Doc.');
      return;
    }
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      setShowGoogleClientIdError(true);
      return;
    }

    try {
      const docTitle = `US Citrus AI PAA Content - ${row.question}`;
      const documentUrl = await googleDocsService.createDocument(docTitle, contentToCopy);
      window.open(documentUrl, '_blank');
    } catch (error) {
      console.error('Error creating Google Doc:', error);
      window.open('https://docs.google.com/', '_blank');
    }
  };

  const generateUrlSlug = (keyword: string): string =>
    keyword.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

  /**
   * Determine secondary category based on content analysis
   * Why this matters: Automatically categorizes content for better organization and content strategy.
   */
  const determineSecondaryCategory = (keyword: string, content: string): string => {
    const keywordLower = keyword.toLowerCase();
    const contentLower = content.toLowerCase();
    const combinedText = `${keywordLower} ${contentLower}`;

    // Define category keywords for matching
    const categoryKeywords = {
      'Sales': ['sales', 'selling', 'prospect', 'deal', 'revenue', 'quota', 'pipeline', 'conversion', 'closing', 'negotiation'],
      'Prospecting': ['prospecting', 'outreach', 'cold email', 'cold call', 'lead generation', 'qualification', 'discovery'],
      'Marketing': ['marketing', 'campaign', 'brand', 'advertising', 'promotion', 'content marketing', 'social media'],
      'CRM': ['crm', 'customer relationship', 'salesforce', 'hubspot', 'pipedrive', 'contact management'],
      'Revenue Operations': ['revops', 'revenue operations', 'sales ops', 'operations', 'process', 'workflow', 'automation'],
      'GTM': ['gtm', 'go-to-market', 'market entry', 'launch', 'strategy', 'positioning'],
      'Data': ['data', 'analytics', 'metrics', 'reporting', 'insights', 'intelligence', 'dashboard'],
      'AI': ['ai', 'artificial intelligence', 'machine learning', 'automation', 'chatbot', 'predictive'],
      'B2B': ['b2b', 'business to business', 'enterprise', 'saas', 'software'],
      'Demand Generation': ['demand gen', 'lead gen', 'inbound', 'content', 'seo', 'paid ads'],
      'Content Marketing': ['content', 'blog', 'article', 'video', 'podcast', 'webinar'],
      'Marketing Strategy': ['strategy', 'planning', 'budget', 'roi', 'attribution'],
      'Sales Intelligence': ['intelligence', 'research', 'data enrichment', 'contact discovery'],
      'Compliance Data Strategy': ['compliance', 'gdpr', 'privacy', 'regulation', 'legal'],
    };

    // Count matches for each category
    let maxMatches = 0;
    let selectedCategory = 'General';

    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      let matches = 0;
      for (const keyword of keywords) {
        if (combinedText.includes(keyword)) {
          matches++;
        }
      }
      if (matches > maxMatches) {
        maxMatches = matches;
        selectedCategory = category;
      }
    }

    return selectedCategory;
  };

  /**
   * Randomly select author from predefined list
   * Why this matters: Ensures author attribution is distributed across the team for content variety.
   */
  const selectRandomAuthor = (): string => {
    const authors = [
      'shaun-hinklein',
      'andy-mccotter-bicknell', 
      'cam-thompson',
      'kenny-keesee',
      'maribeth-daytona',
      'melanie-maecardeno'
    ];
    
    const randomIndex = Math.floor(Math.random() * authors.length);
    return authors[randomIndex];
  };

  const openGoogleSheets = async () => {
    const contentToCopy = isEditingContent ? editableContent : generatedContent || row.output || '';
    if (!contentToCopy) {
      alert('Please generate content first before logging to Google Sheets.');
      return;
    }
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      setShowGoogleClientIdError(true);
      return;
    }

    setIsOpeningSheets(true);
    try {
      // Generate the new metadata fields
      const urlSlug = generateUrlSlug(row.question);
      const secondaryCategory = determineSecondaryCategory(row.question, contentToCopy);
      const author = selectRandomAuthor();

      // Prepare blog content data for logging with new fields (keyword removed)
      const blogData = {
        seoTitle: seoTitle || `${row.question} - Complete Guide`,
        seoDescription: seoDescription || `Comprehensive guide about ${row.question} with expert insights and actionable tips.`,
        htmlContent: contentToCopy,
        urlSlug: urlSlug,
        secondaryCategory: secondaryCategory,
        author: author
      };
      
      // Use the competitor-specific function which creates/uses a spreadsheet titled
      // "US Citrus AI Outrank Competitors Content - {date}"
      const result = await (googleDocsService as any).appendCompetitorData(blogData);
      
      if (result.success) {
        setSheetsSuccessMessage('Content logged to Google Sheets successfully!');
        setShowSheetsMessage(true);
        setTimeout(() => setShowSheetsMessage(false), 3000);
        window.open(result.spreadsheetUrl, '_blank');
      }
    } catch (error) {
      console.error('Error logging to Google Sheets:', error);
      
      // Show more detailed error message
      if (error instanceof Error) {
        alert(`Google Sheets error: ${error.message}`);
      } else {
        alert('Google Sheets error. Please try again.');
      }
    } finally {
      setIsOpeningSheets(false);
    }
  };

  /**
   * publishToCustomCMS
   * Why this matters: Enables publishing content directly to Shopify store as a blog article
   * This gives users a seamless workflow from content generation to publication
   */
  const publishToCustomCMS = async (status: 'draft' | 'published' = 'draft') => {
    if (!generatedContent || !seoTitle) {
      alert('Please generate content first');
      return;
    }

    if (!customCMSConfig.shop_name || !customCMSConfig.access_token || !customCMSConfig.blog_id) {
      setShowValidationError(true);
      return;
    }

    setIsPublishing(true);
    setPublishResult(null);

    try {
      console.log('📰 Publishing content to Shopify:', customCMSConfig);

      // Build Shopify API endpoint
      // Why this matters: Shopify uses format https://{shop}.myshopify.com/admin/api/2024-01/blogs/{blog_id}/articles.json
      const shopifyEndpoint = `https://${customCMSConfig.shop_name}.myshopify.com/admin/api/2024-01/blogs/${customCMSConfig.blog_id}/articles.json`;

      // Use centralized API configuration
      // Why this matters: Ensures all deployments (Netlify, Vercel, local) use the correct backend URL
        
      const response = await fetch(buildApiUrl('/api/content/publish-to-cms'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: seoTitle,
          content: generatedContent,
          meta_title: seoTitle,
          meta_description: seoDescription,
          api_endpoint: shopifyEndpoint,
          api_key: customCMSConfig.access_token,
          cms_type: 'shopify',
          shop_name: customCMSConfig.shop_name,
          blog_id: customCMSConfig.blog_id,
          status: status
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to publish content');
      }

      const data = await response.json();
      
      setPublishResult({
        success: true,
        ...data.publication,
        demo_mode: data.demo_mode
      });

      console.log('✅ Publish successful:', data);

    } catch (error) {
      console.error('Publication error:', error);
      setPublishResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsPublishing(false);
    }
  };

  /**
   * clearGeneratedContent
   * Why this matters: Lets users reset output area, matching the Blog modal affordance.
   */
  const clearGeneratedContent = () => {
    setGeneratedContent('');
    setEditableContent('');
    setIsEditingContent(false);
    onContentUpdate?.(row.id, '');
  };

  /**
   * dismissVocKitReady
   * Why this matters: Allows users to dismiss the VoC Kit ready notification.
   */
  const dismissVocKitReady = () => {
    setVocKitReadyDismissed(true);
    localStorage.setItem('us_citrus_voc_kit_ready_dismissed', 'true');
  };

  /**
   * Save current state to edit history (copied from BlogContentActionModal)
   * Why this matters: Enables undo/redo functionality by tracking content changes.
   */
  const saveToHistory = (content: string) => {
    const newHistory = editHistory.slice(0, historyIndex + 1);
    newHistory.push(content);
    setEditHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  /**
   * Initialize edit history when entering edit mode (copied from BlogContentActionModal)
   * Why this matters: Sets up the baseline for undo/redo operations.
   */
  const initializeEditHistory = (content: string) => {
    setEditHistory([content]);
    setHistoryIndex(0);
  };

  /**
   * Count inserted CTAs in current content (copied from BlogContentActionModal)
   * Why this matters: Provides feedback on how many CTAs have been inserted.
   */
  const countInsertedCTAs = (): number => {
    return countQuizCTAs(editableContent);
  };

  /**
   * Insert CTA at cursor position in edit mode (copied from BlogContentActionModal)
   * Why this matters: Allows users to insert CTAs exactly where they want them in the content.
   */
  const insertCTAAtCursor = (position: 'beginning' | 'middle' | 'end') => {
    if (!generatedCTAs || !editableContentRef.current) return;

    const textarea = editableContentRef.current;
    const cursorPosition = textarea.selectionStart;
    const ctaData = generatedCTAs.cta_variants[position];

    const scrollTop = textarea.scrollTop;
    const scrollLeft = textarea.scrollLeft;

    const fullStyledCTA = getStyledCtaHtml(position);
    const ctaWithComment = `\n\n<!-- Quiz CTA: ${position.toUpperCase()} - ${ctaData.cta.headline} -->\n${fullStyledCTA}\n<!-- End Quiz CTA -->\n\n`;

    const beforeCursor = editableContent.substring(0, cursorPosition);
    const afterCursor = editableContent.substring(cursorPosition);
    const newContent = beforeCursor + ctaWithComment + afterCursor;

    saveToHistory(newContent);
    setEditableContent(newContent);

    setTimeout(() => {
      const newCursorPosition = cursorPosition + ctaWithComment.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      textarea.scrollTop = scrollTop;
      textarea.scrollLeft = scrollLeft;

      setTimeout(() => {
        if (textarea.scrollTop !== scrollTop) {
          textarea.scrollTop = scrollTop;
          textarea.scrollLeft = scrollLeft;
        }
      }, 100);
    }, 50);

    setCtaCopySuccess(`${position}_inserted`);
    setTimeout(() => setCtaCopySuccess(''), 2000);
  };

  /**
   * Undo last change in edit mode (copied from BlogContentActionModal)
   * Why this matters: Allows users to revert unwanted changes without losing all their work.
   */
  const undoChange = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setEditableContent(editHistory[newIndex]);
      
      // Focus back to textarea
      setTimeout(() => {
        if (editableContentRef.current) {
          editableContentRef.current.focus();
        }
      }, 50);

      console.log(`↶ Undo: Reverted to history state ${newIndex}`);
    }
  };

  /**
   * Redo last undone change in edit mode (copied from BlogContentActionModal)
   * Why this matters: Allows users to restore changes they accidentally undid.
   */
  const redoChange = () => {
    if (historyIndex < editHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setEditableContent(editHistory[newIndex]);
      
      // Focus back to textarea
      setTimeout(() => {
        if (editableContentRef.current) {
          editableContentRef.current.focus();
        }
      }, 50);

      console.log(`↷ Redo: Advanced to history state ${newIndex}`);
    }
  };

  /**
   * Clear all CTAs from the current content (copied from BlogContentActionModal)
   * Why this matters: Provides a quick way to remove all inserted CTAs and start fresh.
   */
  const clearAllCTAs = () => {
    if (!generatedCTAs) return;

    const clearedContent = clearQuizCTAs(editableContent);
    saveToHistory(clearedContent);
    setEditableContent(clearedContent);

    setTimeout(() => {
      if (editableContentRef.current) {
        editableContentRef.current.focus();
      }
    }, 50);
  };

  /**
   * Decode HTML entities to fix display issues
   * Why this matters: Content might be HTML-encoded, causing raw HTML to display as text.
   */
  const decodeHtmlEntities = (text: string): string => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  /**
   * Get display content with CTA styling applied (copied from BlogContentActionModal)
   * Why this matters: Shows styled CTAs in the preview while maintaining shortcodes in edit mode.
   */
  const getDisplayContent = (): string => {
    if (isEditingContent) {
      // In edit mode, show the editable content directly (now contains full HTML CTAs)
      return editableContent;
    } else {
      // In display mode, return the saved content directly (already styled during save)
      // Decode HTML entities in case content is encoded
      const content = generatedContent || '';
      return decodeHtmlEntities(content);
    }
  };

  /**
   * Format HTML content for better readability in edit mode (copied from BlogContentActionModal)
   * Why this matters: Breaks up condensed HTML into readable lines for easier editing.
   */
  const formatHTMLForEditing = (content: string): string => {
    if (!content) return content;

    let formatted = content
      // Add line breaks after closing tags
      .replace(/(<\/h[1-6]>)/g, '$1\n\n')
      .replace(/(<\/p>)/g, '$1\n\n')
      .replace(/(<\/div>)/g, '$1\n')
      .replace(/(<\/li>)/g, '$1\n')
      .replace(/(<\/ul>)/g, '$1\n\n')
      .replace(/(<\/ol>)/g, '$1\n\n')
      .replace(/(<\/table>)/g, '$1\n\n')
      .replace(/(<\/thead>)/g, '$1\n')
      .replace(/(<\/tbody>)/g, '$1\n')
      .replace(/(<\/tr>)/g, '$1\n')
      .replace(/(<\/blockquote>)/g, '$1\n\n')
      .replace(/(<\/section>)/g, '$1\n\n')
      .replace(/(<\/article>)/g, '$1\n\n')
      
      // Add line breaks before opening tags
      .replace(/(<h[1-6][^>]*>)/g, '\n$1')
      .replace(/(<p[^>]*>)/g, '\n$1')
      .replace(/(<ul[^>]*>)/g, '\n$1')
      .replace(/(<ol[^>]*>)/g, '\n$1')
      .replace(/(<li[^>]*>)/g, '\n  $1')
      .replace(/(<table[^>]*>)/g, '\n$1')
      .replace(/(<thead[^>]*>)/g, '\n  $1')
      .replace(/(<tbody[^>]*>)/g, '\n  $1')
      .replace(/(<tr[^>]*>)/g, '\n    $1')
      .replace(/(<th[^>]*>)/g, '$1')
      .replace(/(<td[^>]*>)/g, '$1')
      .replace(/(<blockquote[^>]*>)/g, '\n$1')
      .replace(/(<div[^>]*>)/g, '\n$1')
      
      // Clean up multiple consecutive newlines
      .replace(/\n{3,}/g, '\n\n')
      
      // Trim leading/trailing whitespace
      .trim();

    return formatted;
  };

  /**
   * Convert formatted HTML back to compact format for saving (copied from BlogContentActionModal)
   * Why this matters: Removes extra whitespace and formatting added for editing readability.
   */
  const compactHTMLForSaving = (content: string): string => {
    if (!content) return content;

    return content
      // Remove extra newlines and indentation
      .replace(/\n\s+/g, '\n')
      .replace(/\n{2,}/g, '\n')
      .replace(/>\s+</g, '><')
      .trim();
  };

  /**
   * Generate full styled HTML for a specific CTA variant (EXACTLY matching generated CTA display)
   * Why this matters: Creates the complete US Citrus AI–styled CTA HTML that matches the preview exactly.
   */
  const getStyledCtaHtml = (position: 'beginning' | 'middle' | 'end'): string => {
    if (!generatedCTAs) return '';
    const ctaData = generatedCTAs.cta_variants[position];
    const quizUrl = getQuizUrl(position);
    return getQuizCtaHtml(ctaData, quizUrl);
  };

  /**
   * Convert shortcodes to full HTML styling (copied from BlogContentActionModal)
   * Why this matters: Converts shortcodes to full HTML styling when content is saved or displayed.
   */
  const convertShortcodesToFullStyling = (content: string): string => {
    if (!content || !generatedCTAs) return content;

    let processedContent = content;
    let hasCTA = false;
    Object.entries(generatedCTAs.cta_variants).forEach(([position, ctaData]: [string, QuizCtaVariant]) => {
      const shortcodePattern = new RegExp(ctaData.shortcode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      if (shortcodePattern.test(processedContent)) hasCTA = true;
      const quizUrl = getQuizUrl(position);
      const fullStyledCTA = getQuizCtaHtml(ctaData, quizUrl);
      processedContent = processedContent.replace(shortcodePattern, fullStyledCTA);
    });
    if (hasCTA && !processedContent.includes('quizCtaFade')) {
      processedContent = getQuizCtaStyles() + '\n' + processedContent;
    }
    return processedContent;
  };

  const getQuizUrl = (position: string): string => {
    return generateQuizUtmUrl(row.question, position);
  };

  const cycleCTAButton = (position: 'beginning' | 'middle' | 'end') => {
    if (!generatedCTAs) return;

    const currentButton = generatedCTAs.cta_variants[position].cta.action_button;
    const currentIndex = QUIZ_BUTTON_OPTIONS.indexOf(currentButton);
    const nextIndex = (currentIndex + 1) % QUIZ_BUTTON_OPTIONS.length;
    const newButton = QUIZ_BUTTON_OPTIONS[nextIndex];

    const updated = {
      ...generatedCTAs,
      cta_variants: {
        ...generatedCTAs.cta_variants,
        [position]: {
          ...generatedCTAs.cta_variants[position],
          cta: {
            ...generatedCTAs.cta_variants[position].cta,
            action_button: newButton
          }
        }
      }
    };
    setGeneratedCTAs(updated);
    try {
      localStorage.setItem(`quiz_ctas_${row.id}`, JSON.stringify({ ctas: updated, variantIndex: quizVariantIndex }));
    } catch {}
  };

  const generateCTAs = () => {
    if (!generatedContent.trim()) {
      setCtaError('Please generate content first');
      return;
    }

    setIsGeneratingCTAs(true);
    setCtaError('');

    setTimeout(() => {
      const idx = generatedCTAs ? quizVariantIndex + 1 : 0;
      const ctaData = generateQuizCTAs(idx);
      setQuizVariantIndex(idx);
      setGeneratedCTAs(ctaData);
      setIsGeneratingCTAs(false);

      try {
        localStorage.setItem(`quiz_ctas_${row.id}`, JSON.stringify({ ctas: ctaData, variantIndex: idx }));
      } catch {}
    }, 400);
  };


  /**
   * copyCtaToClipboard (copied from BlogContentActionModal)
   * Why this matters: Provides feedback when copying CTA HTML code.
   */
  const copyCtaToClipboard = async (text: string, position: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCtaCopySuccess(position);
      setTimeout(() => setCtaCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy CTA:', err);
    }
  };

  /**
   * handleGenerate
   * Why this matters: Regenerates content for this keyword+URL using the tailored prompts with brand kit processing.
   */
  const handleGenerate = async () => {
    // Try to load brand kit, but allow generation even without one (backend will use defaults)
    let kit = resolvedBrandKit;
    if (!kit) {
      try {
        const draft = localStorage.getItem('us_citrus_brand_kit_draft');
        const saved = localStorage.getItem('us_citrus_brand_kit');
        const data = draft || saved;
        if (data) kit = JSON.parse(data);
      } catch {}
    }
    
    // If still no brand kit, use a minimal default one
    if (!kit) {
      console.log('⚠️ No brand kit found, using default US Citrus brand values');
      kit = {
        url: 'https://uscitrusnursery.com',
        aboutBrand: 'US Citrus Nursery is a leading provider of premium citrus trees',
        idealCustomerProfile: 'Home gardeners, citrus enthusiasts, and commercial growers',
        competitors: 'Other citrus nurseries and garden centers',
        brandPointOfView: 'Every gardener deserves access to high-quality citrus trees',
        authorPersona: 'Citrus growing expert',
        toneOfVoice: 'Professional, knowledgeable, and approachable',
        headerCaseType: 'Title Case',
        writingRules: 'Clear, concise, actionable content with expert insights',
        ctaText: 'Find Your Perfect Tree',
        ctaDestination: 'https://uscitrusnursery.com'
      } as any;
    }
    
    if (!resolvedBrandKit) {
      setResolvedBrandKit(kit);
    }

    // Load sitemap data from localStorage for internal linking
    let sitemapData = null;
    try {
      const stored = localStorage.getItem('us_citrus_sitemap_data');
      if (stored) {
        const sitemaps = JSON.parse(stored);
        // Flatten all sitemap URLs into a single array for content generation
        const allUrls = sitemaps.flatMap((sitemap: any) => 
          sitemap.urls.map((url: any) => ({
            title: url.title,
            description: url.description,
            url: url.url
          }))
        );
        sitemapData = allUrls;
        console.log(`🗺️ [CompetitorContentActionModal] Loaded ${allUrls.length} URLs from ${sitemaps.length} sitemaps for internal linking`);
      }
    } catch (error) {
      console.warn('Failed to load sitemap data:', error);
    }

    setIsGenerating(true);
    try {
      // Don't process liquid variables here - let the backend do it with the full context
      // The backend will process these with the brand kit AND inject research/gap analysis context
      console.log('🚀 Sending prompts to backend for competitor content generation');
      console.log('📝 System prompt preview:', systemPrompt.substring(0, 200));
      console.log('📝 User prompt preview:', userPrompt.substring(0, 200));
      console.log('🎯 Brand kit:', kit);

      // Use streaming content generation with built-in contextual CTAs for competitor content
      console.log('🚀 [CompetitorContentActionModal] Using streaming content generation approach...');
      
      if (enableContextualCtas) {
        setIsEnhancingWithCtas(true);
        console.log('🎯 [CompetitorContentActionModal] Streaming generation with contextual CTAs enabled');
      }
      
      const productUrlForApi = `https://uscitrusnursery.com/products/${row.treeSlug}`;
      const resp = await fetch(API_ENDPOINTS.paaContentGenerateAsync, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: row.question,
          treeName: row.treeName,
          treeSlug: row.treeSlug,
          productUrl: productUrlForApi,
          blogUrl: `https://uscitrusnursery.com/blogs/citrus-simplified/${row.treeSlug}`,
          brand_kit: kit || undefined,
          sitemap_data: sitemapData,
          system_prompt: systemPrompt,
          user_prompt: userPrompt
        })
      });

      const json = await resp.json();
      const data = json?.data || json;
      
      // Store workflow data if available (deep research and gap analysis)
      if (data?.workflow_data) {
        console.log('📊 Storing workflow data for future regenerations');
        setWorkflowData(data.workflow_data);
        
        // Also store in localStorage for persistence
        localStorage.setItem(`us_citrus_competitor_workflow_${row.id}`, JSON.stringify(data.workflow_data));
      }
      
      const payload = data?.content ?? data?.variations ?? '';
      const flattened = Array.isArray(payload) ? (payload[0] || '') : String(payload || '');
      const parsed = parseAIResponse(flattened);
      
      // Convert to HTML if content appears to be in markdown format
      let displayContent = parsed.content;
      const hasMarkdownSyntax = parsed.content.includes('**') || 
                               parsed.content.includes('# ') || 
                               parsed.content.includes('## ') ||
                               parsed.content.includes('* ') ||
                               parsed.content.includes('[') && parsed.content.includes('](');
      const hasHTMLTags = parsed.content.includes('<h1>') || 
                         parsed.content.includes('<p>') || 
                         parsed.content.includes('<strong>');
      
      console.log('🔄 Cleaning newly generated AI content using BlogContentActionModal logic');
      console.log('🔍 Before cleaning (first 500 chars):', parsed.content.substring(0, 500));
      displayContent = cleanAIContent(parsed.content);
      console.log('🔍 After cleaning (first 500 chars):', displayContent.substring(0, 500));
      
      // Handle streaming CTA results if available
      if (data.ctaInsertions && enableContextualCtas) {
        setCtaEnhancementResult({
          totalCtasInserted: data.ctaInsertions.totalInserted,
          insertionPoints: data.ctaInsertions.insertionPoints.map((point: any) => ({
            paragraphIndex: point.paragraphIndex,
            ctaText: point.cta,
            reasoning: point.reasoning,
            confidence: point.confidence
          })),
          processingTimeMs: data.processingTimeMs
        });
        console.log(`✅ [CompetitorContentActionModal] Streaming generation inserted ${data.ctaInsertions.totalInserted} contextual CTAs`);
      }
      
      setIsEnhancingWithCtas(false);
      
      setGeneratedContent(displayContent);
      setEditableContent(parsed.content); // Keep original format for editing
      setIsEditingContent(false);
      
      // Set SEO fields from parsed content or row metadata
      if (parsed.seoTitle) {
        setSeoTitle(parsed.seoTitle);
      } else if (row.metadata?.seoTitle || row.seoTitle) {
        setSeoTitle(row.metadata?.seoTitle || row.seoTitle || '');
      }
      
      if (parsed.seoDescription) {
        setSeoDescription(parsed.seoDescription);
      } else if (row.metadata?.seoDescription || row.seoDescription) {
        setSeoDescription(row.metadata?.seoDescription || row.seoDescription || '');
      }
      
      // Save SEO fields to localStorage for persistence
      if (seoTitle || seoDescription) {
        try {
          localStorage.setItem(`us_citrus_paa_meta_${row.id}`, JSON.stringify({
            seoTitle,
            seoDescription
          }));
        } catch {}
      }
      
      onContentUpdate?.(row.id, displayContent);
    } catch (e) {
      console.error('Regenerate failed:', e);
      alert(`Generation failed: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * insertVariable
   * Why this matters: Inserts variables preserving undo stack, mirroring Blog modal UX.
   */
  const insertVariable = (variableValue: string) => {
    const textarea = activePromptField === 'system' ? systemRef.current : userRef.current;
    if (!textarea) return;
    textarea.focus();
    if (document.execCommand) {
      document.execCommand('insertText', false, ` ${variableValue} `);
      // Sync controlled state with the DOM value to guarantee insertion persists
      const updated = textarea.value;
      if (activePromptField === 'system') setSystemPrompt(updated);
      else setUserPrompt(updated);
    } else {
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const currentValue = activePromptField === 'system' ? systemPrompt : userPrompt;
      const newValue = currentValue.substring(0, start) + ` ${variableValue} ` + currentValue.substring(end);
      if (activePromptField === 'system') setSystemPrompt(newValue);
      else setUserPrompt(newValue);
      setTimeout(() => {
        const newPosition = start + variableValue.length + 2;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
      }, 0);
    }
    setShowVariablesMenu(false);
    setVariableSearchTerm('');
  };

  /**
   * handleVariablesMenuToggle
   * Why this matters: Positions the variables menu beside the clicked button for parity with Blog modal.
   */
  const handleVariablesMenuToggle = (promptField: 'system' | 'user') => {
    setActivePromptField(promptField);
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      setVariablesButtonPosition({ top: window.innerHeight / 2, left: window.innerWidth / 2 });
    } else {
      const buttonRef = promptField === 'system' ? systemVariablesButtonRef.current : userVariablesButtonRef.current;
      if (buttonRef) {
        const rect = buttonRef.getBoundingClientRect();
        setVariablesButtonPosition({ top: rect.top + window.scrollY, left: rect.right + window.scrollX + 12 });
      }
    }
    setShowVariablesMenu(!showVariablesMenu);
    setVariableSearchTerm('');
  };

  // Close variables menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const menu = variablesMenuRef.current;
      if (menu && (event.target instanceof Node) && !menu.contains(event.target)) {
        setShowVariablesMenu(false);
      }
    };
    // Use capture to ensure we see the event before the outer backdrop
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  // Inject generated-content styles (parity with Blog modal)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .generated-content-display { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; line-height: 1.6; color: #374151; }
      .generated-content-display h1 { font-size: 2rem; font-weight: 700; color: #111827; margin: 0 0 1.5rem 0; line-height: 1.2; border-bottom: 0.125rem solid #f3f4f6; padding-bottom: 0.75rem; }
      .generated-content-display h2 { font-size: 1.5rem; font-weight: 600; color: #1f2937; margin: 2rem 0 1rem 0; line-height: 1.3; }
      .generated-content-display h3 { font-size: 1.25rem; font-weight: 600; color: #374151; margin: 1.5rem 0 0.75rem 0; line-height: 1.4; }
      .generated-content-display p { margin: 0 0 1.25rem 0; line-height: 1.7; }
      .generated-content-display ul, .generated-content-display ol { margin: 1rem 0; padding-left: 1.5rem; }
      .generated-content-display li { margin: 0.5rem 0; line-height: 1.6; }
      .generated-content-display table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; border: 1px solid #e5e7eb; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); }
      .generated-content-display thead { background-color: #f9fafb; }
      .generated-content-display th { padding: 0.875rem 1rem; text-align: left; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb; font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.05em; }
      .generated-content-display td { padding: 0.875rem 1rem; border-bottom: 1px solid #f3f4f6; color: #6b7280; font-size: 0.875rem; line-height: 1.5; }
      .generated-content-display tbody tr:hover { background-color: #f9fafb; }
      .generated-content-display tbody tr:last-child td { border-bottom: none; }
      .generated-content-display a { color: #2563eb; text-decoration: underline; font-weight: 500; }
      .generated-content-display a:hover { color: #1d4ed8; text-decoration: underline; }
    `;
    document.head.appendChild(style);
    return () => { if (document.head.contains(style)) document.head.removeChild(style); };
  }, []);

  // Don't render if modal is not open
  if (!isOpen) return null;

  return (
    <>
      {autoSaveStatus && (
        <div
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1rem',
            backgroundColor: 'white',
            border: '0.0625rem solid #e5e7eb',
            borderRadius: '0.5rem',
            boxShadow: '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1), 0 0.125rem 0.25rem -0.0625rem rgba(0, 0, 0, 0.06)',
            color: autoSaveStatus === 'saving' ? '#6b7280' : '#10b981',
            fontSize: '0.875rem',
            fontWeight: 500,
            pointerEvents: 'none'
          }}
        >
          {autoSaveStatus === 'saving' ? (
            <>
              <Clock className="animate-spin" style={{ width: '0.75rem', height: '0.75rem' }} />
              Auto-saving prompts...
            </>
          ) : (
            <>
              <CheckCircle size={14} />
              Prompts auto-saved
            </>
          )}
        </div>
      )}
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem' }}
      >
      <div
        style={{ backgroundColor: 'white', borderRadius: '0.75rem', width: '100%', maxWidth: '95vw', height: '95vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        className="content-creation-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Match BlogContentActionModal exactly */}
        <div 
          className="content-modal-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem',
            borderBottom: '0.0625rem solid #e5e7eb',
            backgroundColor: '#fafafa',
            position: 'relative'
          }}
        >
          <div>
            <h2 style={{ fontWeight: 600, margin: 0, color: '#111827' }}>People Also Ask Content Actions</h2>
            <p style={{ color: '#6b7280' }}>Edit, publish, and manage your generated People Also Ask content for "{row.question}" ({row.treeName})</p>
          </div>
          <button
            onClick={onClose}
            className="content-modal-close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content Layout - two panels */}
        <div className="content-modal-layout" style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          {/* Left Panel - CTA Generation Interface (copied from BlogContentActionModal) */}
          <div className="content-modal-panel" style={{ 
            padding: '1.5rem', 
            overflowY: 'auto',
            borderRight: '0.0625rem solid #e5e7eb'
          }}>
            {/* Quiz CTA Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{
                padding: '0.5rem',
                background: '#4E960C',
                borderRadius: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Target size={24} style={{ color: '#FFD93D' }} />
              </div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0, color: '#374151' }}>
                Generate Quiz CTAs
              </h3>
            </div>

            <p style={{ 
              fontSize: '0.875rem', 
              color: '#64748b', 
              margin: '0 0 1.5rem 0',
              lineHeight: '1.5'
            }}>
              {generatedCTAs 
                ? 'Regenerate with fresh copy variations.'
                : 'Create beautiful CTAs that promote the US Citrus quiz and help readers find their perfect citrus tree.'
              }
            </p>

            {/* Generate Quiz CTAs Button */}
            <button
              onClick={generateCTAs}
              disabled={isGeneratingCTAs || !generatedContent.trim()}
              style={{
                width: '100%',
                padding: '1rem 1.5rem',
                background: (isGeneratingCTAs || !generatedContent.trim()) ? '#9ca3af' : '#4E960C',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: (isGeneratingCTAs || !generatedContent.trim()) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                transition: 'all 0.2s ease',
                marginBottom: '1.5rem'
              }}
            >
              {isGeneratingCTAs ? (
                <>
                  <div style={{
                    width: '1rem',
                    height: '1rem',
                    border: '0.125rem solid transparent',
                    borderTop: '0.125rem solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Generating Quiz CTAs...
                </>
              ) : (
                <>
                  <Target size={20} strokeWidth={3} />
                  {generatedCTAs ? 'Regenerate Quiz CTAs' : 'Generate Quiz CTAs'}
                </>
              )}
            </button>

            {ctaError && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '0.75rem',
                backgroundColor: '#fee2e2',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <AlertCircle size={16} />
                {ctaError}
              </div>
            )}

            {/* Generated Quiz CTAs Display */}
            {generatedCTAs && (
              <QuizCtaPreviewCards
                ctaVariants={generatedCTAs.cta_variants}
                onCycleCTAButton={cycleCTAButton}
                getStyledCtaHtml={getStyledCtaHtml}
                getQuizUrl={getQuizUrl}
                ctaCopySuccess={ctaCopySuccess}
                onCopyCtaToClipboard={copyCtaToClipboard}
              />
            )}

              {/* Divider */}
              <div style={{ 
                borderTop: '1px solid #e5e7eb', 
                margin: '2rem 0' 
              }} />

              {/* Image Generation Interface */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <div style={{
                  padding: '0.5rem',
                  backgroundColor: '#EBF212',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ImageIcon size={24} style={{ color: 'black' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0, color: '#374151' }}>
                    Generate Images
                  </h3>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>
                    Powered by {MODEL_DISPLAY_NAMES.geminiFlashImage}
                  </p>
                </div>
              </div>

              <p style={{ 
                fontSize: '0.875rem', 
                color: '#64748b', 
                margin: '0 0 1.5rem 0',
                lineHeight: '1.5'
              }}>
                Generate 3 unique images (beginning, middle, ending) for your FAQ article. Use the "Insert" button to add them to the content editor.
              </p>

              {/* Image Generation Button */}
              <button
                onClick={generateImages}
                disabled={isGeneratingImages || !(generatedContent || editableContent).trim()}
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  backgroundColor: (isGeneratingImages || !(generatedContent || editableContent).trim()) ? '#9ca3af' : '#EBF212',
                  color: (isGeneratingImages || !(generatedContent || editableContent).trim()) ? 'white' : 'black',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: (isGeneratingImages || !(generatedContent || editableContent).trim()) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.2s ease',
                  marginBottom: '1.5rem'
                }}
              >
                {isGeneratingImages ? (
                  <>
                    <div style={{
                      width: '1rem',
                      height: '1rem',
                      border: '0.125rem solid transparent',
                      borderTop: '0.125rem solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Generating Images...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} strokeWidth={2} />
                    {generatedImages.length > 0 ? 'Regenerate Images' : 'Generate Images'}
                  </>
                )}
              </button>

              {imageError && (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#fee2e2',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <AlertCircle size={16} />
                  {imageError}
                </div>
              )}

              {/* Generated Images List */}
              {generatedImages.length > 0 && (
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {generatedImages.map((img, index) => (
                    <div key={index} style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.75rem',
                      padding: '1rem',
                      backgroundColor: '#fafafa'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '0.75rem'
                      }}>
                        <div style={{ 
                          fontSize: '0.875rem', 
                          fontWeight: '600', 
                          color: '#374151',
                          textTransform: 'capitalize' 
                        }}>
                          {img.placement} Image
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {/* Insert Button */}
                          <button
                            onClick={() => insertImageAtCursor(img)}
                            disabled={!isEditingContent}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: imageCopySuccess === `${img.placement}_inserted` ? '#dcfce7' : (isEditingContent ? '#dbeafe' : '#f3f4f6'),
                              color: imageCopySuccess === `${img.placement}_inserted` ? '#16a34a' : (isEditingContent ? '#1e40af' : '#9ca3af'),
                              border: `1px solid ${imageCopySuccess === `${img.placement}_inserted` ? '#bbf7d0' : (isEditingContent ? '#93c5fd' : '#d1d5db')}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: isEditingContent ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              transition: 'all 0.2s ease'
                            }}
                            title={isEditingContent ? `Insert ${img.placement} image at cursor` : 'Enter Edit Mode to insert images'}
                          >
                            {imageCopySuccess === `${img.placement}_inserted` ? (
                              <>
                                <CheckCircle size={12} />
                                Inserted!
                              </>
                            ) : (
                              <>
                                <Target size={12} />
                                Insert
                              </>
                            )}
                          </button>
                          {/* Copy HTML Button */}
                          <button
                            onClick={() => copyImageHtml(img)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: imageCopySuccess === img.placement ? '#dcfce7' : 'white',
                              color: imageCopySuccess === img.placement ? '#16a34a' : '#374151',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {imageCopySuccess === img.placement ? (
                              <>
                                <CheckCircle size={12} />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy size={12} />
                                Copy HTML
                              </>
                            )}
                          </button>
                          {/* Regenerate Button */}
                          <button
                            onClick={() => regenerateSingleImage(img.placement)}
                            disabled={regeneratingPlacement !== null}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: regeneratingPlacement === img.placement ? '#fef3c7' : '#f3f4f6',
                              color: regeneratingPlacement === img.placement ? '#92400e' : '#374151',
                              border: `1px solid ${regeneratingPlacement === img.placement ? '#fcd34d' : '#e5e7eb'}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: regeneratingPlacement !== null ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              transition: 'all 0.2s ease',
                              opacity: regeneratingPlacement !== null && regeneratingPlacement !== img.placement ? 0.5 : 1
                            }}
                            title={`Regenerate ${img.placement} image`}
                          >
                            <RefreshCw 
                              size={12} 
                              style={{ 
                                animation: regeneratingPlacement === img.placement ? 'spin 1s linear infinite' : 'none' 
                              }} 
                            />
                            {regeneratingPlacement === img.placement ? 'Regenerating...' : 'Regenerate'}
                          </button>
                        </div>
                      </div>

                      {/* Image container with skeleton loading */}
                      <div style={{
                        borderRadius: '0.5rem',
                        overflow: 'hidden',
                        border: '1px solid #e5e7eb',
                        marginBottom: '0.75rem',
                        backgroundColor: '#f3f4f6',
                        position: 'relative',
                        minHeight: regeneratingPlacement === img.placement ? '200px' : 'auto'
                      }}>
                        {regeneratingPlacement === img.placement ? (
                          <div style={{
                            width: '100%',
                            height: '200px',
                            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 1.5s infinite',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '0.5rem',
                              color: '#9ca3af'
                            }}>
                              <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                              <span style={{ fontSize: '0.875rem' }}>Regenerating image...</span>
                            </div>
                          </div>
                        ) : (
                          <img 
                            src={img.imageUrl || `data:${img.mimeType};base64,${img.base64Data}`} 
                            alt={img.altText}
                            style={{
                              width: '100%',
                              height: 'auto',
                              display: 'block'
                            }}
                          />
                        )}
                      </div>
                      
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: '#6b7280', 
                        fontStyle: 'italic',
                        lineHeight: '1.4'
                      }}>
                        "{img.altText}"
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Divider */}
              <div style={{ 
                borderTop: '1px solid #e5e7eb', 
                margin: '2rem 0' 
              }} />

              {/* Infographic Generation Interface */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <div style={{
                  padding: '0.5rem',
                  backgroundColor: '#10b981',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BarChart3 size={24} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0, color: '#374151' }}>
                    Generate Infographic
                  </h3>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>
                    Powered by {MODEL_DISPLAY_NAMES.geminiFlashImage}
                  </p>
                </div>
              </div>

              <p style={{ 
                fontSize: '0.875rem', 
                color: '#64748b', 
                margin: '0 0 1.5rem 0',
                lineHeight: '1.5'
              }}>
                Generate a branded infographic summarizing key facts, care guide, and benefits of {row.treeName}.
              </p>

              {/* Infographic Generation Button */}
              <button
                onClick={generateInfographic}
                disabled={isGeneratingInfographic || !(generatedContent || editableContent).trim()}
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  backgroundColor: (isGeneratingInfographic || !(generatedContent || editableContent).trim()) ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: (isGeneratingInfographic || !(generatedContent || editableContent).trim()) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.2s ease',
                  marginBottom: '1.5rem'
                }}
              >
                {isGeneratingInfographic ? (
                  <>
                    <div style={{
                      width: '1rem',
                      height: '1rem',
                      border: '0.125rem solid transparent',
                      borderTop: '0.125rem solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Generating Infographic...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} strokeWidth={2} />
                    {generatedInfographic ? 'Regenerate Infographic' : 'Generate Infographic'}
                  </>
                )}
              </button>

              {infographicError && (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#fee2e2',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <AlertCircle size={16} />
                  {infographicError}
                </div>
              )}

              {/* Generated Infographic Display */}
              {generatedInfographic && (
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.75rem',
                  padding: '1rem',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: '600', 
                      color: '#374151'
                    }}>
                      Infographic
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {/* Insert Button */}
                      <button
                        onClick={insertInfographicAtCursor}
                        disabled={!isEditingContent}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: infographicCopySuccess === 'inserted' ? '#dcfce7' : (isEditingContent ? '#dbeafe' : '#f3f4f6'),
                          color: infographicCopySuccess === 'inserted' ? '#16a34a' : (isEditingContent ? '#1e40af' : '#9ca3af'),
                          border: `1px solid ${infographicCopySuccess === 'inserted' ? '#bbf7d0' : (isEditingContent ? '#93c5fd' : '#d1d5db')}`,
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          cursor: isEditingContent ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s ease'
                        }}
                        title={isEditingContent ? 'Insert infographic at cursor' : 'Enter Edit Mode to insert'}
                      >
                        {infographicCopySuccess === 'inserted' ? (
                          <>
                            <CheckCircle size={12} />
                            Inserted!
                          </>
                        ) : (
                          <>
                            <Target size={12} />
                            Insert
                          </>
                        )}
                      </button>
                      {/* Copy HTML Button */}
                      <button
                        onClick={copyInfographicHtml}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: infographicCopySuccess === 'copied' ? '#dcfce7' : 'white',
                          color: infographicCopySuccess === 'copied' ? '#16a34a' : '#374151',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {infographicCopySuccess === 'copied' ? (
                          <>
                            <CheckCircle size={12} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy size={12} />
                            Copy HTML
                          </>
                        )}
                      </button>
                      {/* Regenerate Button */}
                      <button
                        onClick={generateInfographic}
                        disabled={isGeneratingInfographic}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: isGeneratingInfographic ? '#fef3c7' : '#f3f4f6',
                          color: isGeneratingInfographic ? '#92400e' : '#374151',
                          border: `1px solid ${isGeneratingInfographic ? '#fcd34d' : '#e5e7eb'}`,
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          cursor: isGeneratingInfographic ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s ease'
                        }}
                        title="Regenerate infographic"
                      >
                        <RefreshCw 
                          size={12} 
                          style={{ 
                            animation: isGeneratingInfographic ? 'spin 1s linear infinite' : 'none' 
                          }} 
                        />
                        {isGeneratingInfographic ? 'Regenerating...' : 'Regenerate'}
                      </button>
                    </div>
                  </div>

                  {/* Infographic Image */}
                  <div style={{
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                    marginBottom: '0.75rem',
                    backgroundColor: '#f3f4f6'
                  }}>
                    <img 
                      src={generatedInfographic.imageUrl || `data:${generatedInfographic.mimeType};base64,${generatedInfographic.base64Data}`} 
                      alt={generatedInfographic.altText}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </div>
                  
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#6b7280', 
                    fontStyle: 'italic',
                    lineHeight: '1.4'
                  }}>
                    "{generatedInfographic.altText}"
                  </div>
                </div>
              )}
          </div>

          {/* Right Panel - Output & Actions */}
          <div ref={rightPanelRef} className="content-modal-panel" style={{ 
            position: 'relative',
            padding: '1.5rem',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 className="mobile-only-heading" style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: 0, marginBottom: '1rem' }}>
                Generated Content
              </h3>
              
              {/* Clear Button - Centered */}
              {generatedContent && (
                <button
                  onClick={clearGeneratedContent}
                  className="content-modal-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#fef2f2',
                    border: '0.0625rem solid #fecaca',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontFamily: "'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    color: '#dc2626',
                    transition: 'all 0.2s ease',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc2626';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.borderColor = '#dc2626';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#fef2f2';
                    e.currentTarget.style.color = '#dc2626';
                    e.currentTarget.style.borderColor = '#fecaca';
                  }}
                >
                  <X size={14} /> Clear Generated Content
                </button>
              )}
            </div>

            {/* Action Buttons */}
            {generatedContent && (
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                  onClick={toggleEditMode}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.75rem 1rem', 
                    backgroundColor: isEditingContent ? '#10b981' : '#f59e0b', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.5rem', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    fontFamily: "'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    minHeight: '2.75rem',
                    minWidth: '7.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                                  {isEditingContent ? (<><Check size={14} /> Save Changes</>) : (<><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg> Edit Content</>)}
              </button>

              {/* Publish to CMS Button */}
              <button
                onClick={() => setShowCMSModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  fontFamily: "'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  minHeight: '2.75rem',
                  minWidth: '7.5rem',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#059669')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#10b981')}
              >
                <Globe size={14} />
                Publish to CMS
              </button>

              {/* Open in HTML Button */}
              <button
                onClick={openInHTML}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  padding: '0.75rem 1rem', 
                  backgroundColor: '#7c3aed', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '0.5rem', 
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  minHeight: '2.75rem',
                  minWidth: '7.5rem',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#6d28d9';
                  e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#7c3aed';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <svg 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="m6 16 6-12 6 12H6Z"/>
                    <path d="m8 12 8 0"/>
                  </svg>
                  Open in HTML
              </button>

              <button
                onClick={openGoogleDocs}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.75rem 1rem', 
                    backgroundColor: '#84ADEA', 
                    color: 'black', 
                    border: 'none', 
                    borderRadius: '0.5rem', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    minHeight: '2.75rem',
                    minWidth: '12.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#6b96e8';
                    e.currentTarget.style.color = 'black';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#84ADEA';
                    e.currentTarget.style.color = 'black';
                  }}
                >
                  <img 
                    src="/google-docs-logo.png" 
                    alt="Google Docs"
                    style={{
                      width: '14px',
                      height: '14px',
                      objectFit: 'contain'
                    }}
                  />
                  Google Docs
                </button>

                <button
                  onClick={openGoogleSheets}
                  disabled={isOpeningSheets}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.75rem 1rem', 
                    backgroundColor: '#16a34a', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.5rem', 
                    cursor: isOpeningSheets ? 'not-allowed' : 'pointer', 
                    opacity: isOpeningSheets ? 0.6 : 1,
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    minHeight: '2.75rem',
                    minWidth: '7.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isOpeningSheets ? <Clock className="animate-spin" size={14} /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 9v12"/></svg>}
                  {isOpeningSheets ? 'Logging to Sheets...' : 'Open in Google Sheets'}
                </button>

                <button
                  onClick={copyToClipboard}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.75rem 1rem', 
                    backgroundColor: '#6b7280', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.5rem', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    minHeight: '2.75rem',
                    minWidth: '7.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Copy size={14} /> {showCopiedMessage ? 'Copied!' : 'Copy'}
            </button>
              </div>
            )}

            {/* Generated Content Display with SEO fields (FAQ-specific) */}
            {generatedContent ? (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '2rem', minHeight: '25rem', backgroundColor: 'white' }}>
                {(seoTitle || seoDescription) && (
                  <div style={{ marginBottom: '2rem' }}>
                    {seoTitle && (
                      <div style={{ marginBottom: '1rem', position: 'relative' }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong>SEO Title:</strong>
                          <button
                            onClick={copySeoTitle}
                            style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: '#f3f4f6', border: '1px solid #10b981', borderRadius: '0.375rem', cursor: 'pointer', color: '#10b981' }}
                          >
                            Copy
                          </button>
                          {showSeoTitleCopied && (
                            <span style={{ marginLeft: '0.5rem', color: '#10b981', fontSize: '0.75rem' }}>Copied!</span>
                          )}
                        </div>
                        <div style={{ 
                          padding: '0.75rem', 
                          backgroundColor: '#f9fafb', 
                          borderRadius: '0.375rem', 
                          border: '1px solid #e5e7eb', 
                          wordWrap: 'break-word', 
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.5'
                        }}>
                          {seoTitle}
                        </div>
                      </div>
                    )}
                    {seoDescription && (
                      <div style={{ marginBottom: '1rem', position: 'relative' }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong>SEO Description:</strong>
                          <button
                            onClick={copySeoDescription}
                            style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: '#f3f4f6', border: '1px solid #10b981', borderRadius: '0.375rem', cursor: 'pointer', color: '#10b981' }}
                          >
                            Copy
                          </button>
                          {showSeoDescCopied && (
                            <span style={{ marginLeft: '0.5rem', color: '#10b981', fontSize: '0.75rem' }}>Copied!</span>
                          )}
                        </div>
                        <div style={{ 
                          padding: '0.75rem', 
                          backgroundColor: '#f9fafb', 
                          borderRadius: '0.375rem', 
                          border: '1px solid #e5e7eb', 
                          wordWrap: 'break-word', 
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.5'
                        }}>
                          {seoDescription}
                        </div>
                      </div>
                    )}
                    <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '1.5rem 0' }} />
                  </div>
                )}
                {isEditingContent ? (
                  <div style={{ position: 'relative' }}>
                    {/* EDITING MODE Header */}
                    <div style={{
                      padding: '0.75rem 1rem',
                      backgroundColor: '#fef3c7',
                      border: '1px solid #f59e0b',
                      borderRadius: '0.5rem',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      <span style={{ 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#92400e' 
                      }}>
                        🛠️ EDITING MODE - Click "Save Changes" when done
                      </span>
                    </div>

                    {/* CTA Insertion Toolbar */}
                    {generatedCTAs && (
                      <div style={{
                        marginBottom: '1rem',
                        padding: '1rem',
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '0.5rem'
                      }}>
                        <div style={{ 
                          fontSize: '0.875rem', 
                          fontWeight: '600', 
                          color: '#16a34a',
                          marginBottom: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <Target size={16} />
                          Insert CTA at Cursor Position
                        </div>
                        
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: '#374151',
                          marginBottom: '0.75rem',
                          fontStyle: 'italic'
                        }}>
                          Click in the text editor where you want to insert a CTA, then click one of the buttons below:
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          {Object.entries(generatedCTAs.cta_variants).map(([position, ctaData]) => (
                            <button
                              key={position}
                              onClick={() => insertCTAAtCursor(position as 'beginning' | 'middle' | 'end')}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: ctaCopySuccess === `${position}_inserted` ? '#dcfce7' : (position === 'beginning' ? '#dbeafe' : position === 'middle' ? '#fef3c7' : '#dcfce7'),
                                color: ctaCopySuccess === `${position}_inserted` ? '#16a34a' : (position === 'beginning' ? '#1e40af' : position === 'middle' ? '#b45309' : '#16a34a'),
                                border: `1px solid ${ctaCopySuccess === `${position}_inserted` ? '#bbf7d0' : (position === 'beginning' ? '#93c5fd' : position === 'middle' ? '#fcd34d' : '#bbf7d0')}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                textTransform: 'uppercase',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              title={`Insert ${position} CTA: ${(ctaData as QuizCtaVariant).cta.headline}`}
                            >
                              {ctaCopySuccess === `${position}_inserted` ? (
                                <>
                                  <CheckCircle size={12} />
                                  Inserted!
                                </>
                              ) : (
                                <>
                                  <Target size={12} />
                                  Insert {position === 'end' ? 'ending' : position} CTA
                                </>
                              )}
                            </button>
                          ))}
                          
                          {/* CTA Count Display */}
                          {countInsertedCTAs() > 0 && (
                            <div style={{
                              padding: '0.5rem 0.75rem',
                              backgroundColor: '#f0fdf4',
                              border: '1px solid #bbf7d0',
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              color: '#16a34a',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.375rem'
                            }}>
                              <CheckCircle size={12} />
                              {countInsertedCTAs()} CTA{countInsertedCTAs() !== 1 ? 's' : ''} inserted
                            </div>
                          )}
                        </div>

                        {/* Content Management Controls */}
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.5rem', 
                          marginTop: '0.75rem', 
                          paddingTop: '0.75rem',
                          borderTop: '1px solid #e5e7eb',
                          flexWrap: 'wrap',
                          alignItems: 'center'
                        }}>
                          {/* Clear All CTAs Button */}
                          <button
                            onClick={clearAllCTAs}
                            disabled={countInsertedCTAs() === 0}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: countInsertedCTAs() > 0 ? '#fee2e2' : '#f3f4f6',
                              color: countInsertedCTAs() > 0 ? '#dc2626' : '#9ca3af',
                              border: `1px solid ${countInsertedCTAs() > 0 ? '#fecaca' : '#d1d5db'}`,
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: countInsertedCTAs() > 0 ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              if (countInsertedCTAs() > 0) {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.backgroundColor = '#fecaca';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (countInsertedCTAs() > 0) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.backgroundColor = '#fee2e2';
                              }
                            }}
                            title={countInsertedCTAs() > 0 ? 'Remove all inserted CTAs' : 'No CTAs to clear'}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c0-1 1-2 2-2v2"/>
                              <line x1="10" y1="11" x2="10" y2="17"/>
                              <line x1="14" y1="11" x2="14" y2="17"/>
                            </svg>
                            Clear All CTAs
                          </button>

                          {/* Undo Button */}
                          <button
                            onClick={undoChange}
                            disabled={historyIndex <= 0}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: historyIndex > 0 ? '#eff6ff' : '#f3f4f6',
                              color: historyIndex > 0 ? '#2563eb' : '#9ca3af',
                              border: `1px solid ${historyIndex > 0 ? '#bfdbfe' : '#d1d5db'}`,
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: historyIndex > 0 ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              if (historyIndex > 0) {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.backgroundColor = '#dbeafe';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (historyIndex > 0) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.backgroundColor = '#eff6ff';
                              }
                            }}
                            title={historyIndex > 0 ? 'Undo last change' : 'Nothing to undo'}
                          >
                            <RotateCcw size={12} />
                            Undo
                          </button>

                          {/* Redo Button */}
                          <button
                            onClick={redoChange}
                            disabled={historyIndex >= editHistory.length - 1}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: historyIndex < editHistory.length - 1 ? '#f0f9ff' : '#f3f4f6',
                              color: historyIndex < editHistory.length - 1 ? '#0ea5e9' : '#9ca3af',
                              border: `1px solid ${historyIndex < editHistory.length - 1 ? '#bae6fd' : '#d1d5db'}`,
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: historyIndex < editHistory.length - 1 ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              if (historyIndex < editHistory.length - 1) {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.backgroundColor = '#e0f2fe';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (historyIndex < editHistory.length - 1) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.backgroundColor = '#f0f9ff';
                              }
                            }}
                            title={historyIndex < editHistory.length - 1 ? 'Redo last undone change' : 'Nothing to redo'}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" transform="scale(-1, 1)">
                              <path d="M1 4v6h6"/>
                              <path d="m1 10 18-5v12"/>
                            </svg>
                            Redo
                          </button>
                        </div>

                        <div style={{ 
                          fontSize: '0.625rem', 
                          color: '#6b7280',
                          marginTop: '0.5rem',
                          fontStyle: 'italic'
                        }}>
                          💡 CTAs will be inserted as fully styled quiz promotions with immediate visual feedback.
                          <br />
                          🔗 Links will point to: uscitrusnursery.com/pages/quiz with UTM tracking
                        </div>
                      </div>
                    )}
                    
                    <textarea
                      ref={editableContentRef}
                      value={editableContent}
                      onChange={(e) => setEditableContent(e.target.value)}
                      style={{ width: '100%', minHeight: '37.5rem', padding: '1rem 1.25rem', border: '2px solid #f59e0b', borderRadius: '0.5rem', backgroundColor: '#fffbf5', resize: 'vertical', fontFamily: 'inherit', color: '#374151' }}
                    />
                    
                    {/* Bottom CTA Controls - Duplicate of Top Controls */}
                    {generatedCTAs && (
                        <div style={{ 
                          padding: '1rem',
                          marginTop: '1rem',
                          backgroundColor: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          borderRadius: '0.5rem'
                        }}>
                          <div style={{ 
                            fontSize: '0.875rem', 
                            fontWeight: '600', 
                            color: '#16a34a',
                            marginBottom: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <Target size={16} />
                            Insert CTA at Cursor Position
                          </div>
                          
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#374151',
                            marginBottom: '0.75rem',
                            fontStyle: 'italic'
                          }}>
                            Click in the text editor where you want to insert a CTA, then click one of the buttons below:
                          </div>

                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            {Object.entries(generatedCTAs.cta_variants).map(([position, ctaData]) => (
                              <button
                                key={position}
                                onClick={() => insertCTAAtCursor(position as 'beginning' | 'middle' | 'end')}
                                style={{
                                  padding: '0.5rem 1rem',
                                  backgroundColor: ctaCopySuccess === `${position}_inserted` ? '#dcfce7' : (position === 'beginning' ? '#dbeafe' : position === 'middle' ? '#fef3c7' : '#dcfce7'),
                                  color: ctaCopySuccess === `${position}_inserted` ? '#16a34a' : (position === 'beginning' ? '#1e40af' : position === 'middle' ? '#b45309' : '#16a34a'),
                                  border: `1px solid ${ctaCopySuccess === `${position}_inserted` ? '#bbf7d0' : (position === 'beginning' ? '#93c5fd' : position === 'middle' ? '#fcd34d' : '#bbf7d0')}`,
                                  borderRadius: '0.375rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.375rem',
                                  textTransform: 'uppercase',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                                title={`Insert ${position} CTA: ${(ctaData as QuizCtaVariant).cta.headline}`}
                              >
                                {ctaCopySuccess === `${position}_inserted` ? (
                                  <>
                                    <CheckCircle size={12} />
                                    Inserted!
                                  </>
                                ) : (
                                  <>
                                    <Target size={12} />
                                    Insert {position === 'end' ? 'ending' : position} CTA
                                  </>
                                )}
                              </button>
                            ))}
                            
                            {/* CTA Count Display */}
                            {countInsertedCTAs() > 0 && (
                              <div style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                color: '#16a34a',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem'
                              }}>
                                <CheckCircle size={12} />
                                {countInsertedCTAs()} CTA{countInsertedCTAs() !== 1 ? 's' : ''} inserted
                              </div>
                            )}
                          </div>

                          {/* Content Management Controls */}
                          <div style={{ 
                            display: 'flex', 
                            gap: '0.5rem', 
                            marginTop: '0.75rem', 
                            paddingTop: '0.75rem',
                            borderTop: '1px solid #e5e7eb',
                            flexWrap: 'wrap',
                            alignItems: 'center'
                          }}>
                            {/* Clear All CTAs Button */}
                            <button
                              onClick={clearAllCTAs}
                              disabled={countInsertedCTAs() === 0}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: countInsertedCTAs() > 0 ? '#fee2e2' : '#f3f4f6',
                                color: countInsertedCTAs() > 0 ? '#dc2626' : '#9ca3af',
                                border: `1px solid ${countInsertedCTAs() > 0 ? '#fecaca' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: countInsertedCTAs() > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (countInsertedCTAs() > 0) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#fecaca';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (countInsertedCTAs() > 0) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#fee2e2';
                                }
                              }}
                              title={countInsertedCTAs() > 0 ? 'Remove all inserted CTAs' : 'No CTAs to clear'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c0-1 1-2 2-2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                              </svg>
                              Clear All CTAs
                            </button>

                            {/* Undo Button */}
                            <button
                              onClick={undoChange}
                              disabled={historyIndex <= 0}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: historyIndex > 0 ? '#eff6ff' : '#f3f4f6',
                                color: historyIndex > 0 ? '#2563eb' : '#9ca3af',
                                border: `1px solid ${historyIndex > 0 ? '#bfdbfe' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: historyIndex > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (historyIndex > 0) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#dbeafe';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (historyIndex > 0) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#eff6ff';
                                }
                              }}
                              title={historyIndex > 0 ? 'Undo last change' : 'Nothing to undo'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 4v6h6"/>
                                <path d="m1 10 18-5v12"/>
                              </svg>
                              Undo
                            </button>

                            {/* Redo Button */}
                            <button
                              onClick={redoChange}
                              disabled={historyIndex >= editHistory.length - 1}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: historyIndex < editHistory.length - 1 ? '#eff6ff' : '#f3f4f6',
                                color: historyIndex < editHistory.length - 1 ? '#2563eb' : '#9ca3af',
                                border: `1px solid ${historyIndex < editHistory.length - 1 ? '#bfdbfe' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: historyIndex < editHistory.length - 1 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (historyIndex < editHistory.length - 1) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#dbeafe';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (historyIndex < editHistory.length - 1) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#eff6ff';
                                }
                              }}
                              title={historyIndex < editHistory.length - 1 ? 'Redo last undone change' : 'Nothing to redo'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" transform="scale(-1, 1)">
                                <path d="M1 4v6h6"/>
                                <path d="m1 10 18-5v12"/>
                              </svg>
                              Redo
                            </button>
                          </div>

                          <div style={{ 
                            fontSize: '0.625rem', 
                            color: '#6b7280',
                            marginTop: '0.5rem',
                            fontStyle: 'italic'
                          }}>
                            💡 CTAs will be inserted as fully styled quiz promotions with immediate visual feedback.
                            <br />
                            🔗 Links will point to: uscitrusnursery.com/pages/quiz with UTM tracking
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <div
                    className="competitor-content-display"
                    dangerouslySetInnerHTML={{ __html: sanitizeHTMLForDisplay(getDisplayContent()) }}
                    key={`display-${isEditingContent}-${editableContent.length}`}
                  />
                )}
              </div>
            ) : (
              <div style={{ border: '2px dashed #d1d5db', borderRadius: '0.75rem', padding: '4rem 2rem', textAlign: 'center', color: '#6b7280', minHeight: '25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'white' }}>
                <div>
                  <Wand2 size={48} style={{ margin: '0 auto 1rem', opacity: 0.3, display: 'block' }} />
                  <p style={{ fontSize: '1.125rem', fontWeight: 500, marginBottom: '0.5rem' }}>No content generated yet</p>
                  <p style={{ margin: 0 }}>Generate content to see results here</p>
                </div>
              </div>
            )}

            {/* Bottom Action Buttons */}
            {generatedContent && (
              <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '0.0625rem solid #e5e7eb' }}>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {/* Edit/Save */}
                  <button
                    onClick={toggleEditMode}
                    className="content-modal-btn"
                    style={{
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      padding: '0.75rem 1rem',
                      backgroundColor: isEditingContent ? '#10b981' : '#f59e0b', 
                      color: 'white', 
                      border: 'none',
                      borderRadius: '0.5rem', 
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      minHeight: '2.75rem',
                      minWidth: '7.5rem',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                  >
                                      {isEditingContent ? (<><Check size={14} /> Save Changes</>) : (<>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    Edit Content
                  </>)}
                </button>

                {/* Publish to CMS Button */}
                <button
                  onClick={() => setShowCMSModal(true)}
                  className="content-modal-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    minHeight: '2.75rem',
                    minWidth: '7.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#059669')}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#10b981')}
                >
                  <Globe size={14} />
                  Publish to CMS
                </button>

                {/* Open in HTML */}
                <button
                  onClick={openInHTML}
                  className="content-modal-btn"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.75rem 1rem', 
                    backgroundColor: '#7c3aed', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.5rem', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    minHeight: '2.75rem',
                    minWidth: '7.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#6d28d9';
                    e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#7c3aed';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <svg 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="m6 16 6-12 6 12H6Z"/>
                    <path d="m8 12 8 0"/>
                  </svg>
                  Open in HTML
                </button>

                {/* Google Docs */}
                  <button
                    onClick={openGoogleDocs}
                    className="content-modal-btn"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      padding: '0.75rem 1rem', 
                      backgroundColor: '#84ADEA', 
                      color: 'black', 
                      border: 'none', 
                      borderRadius: '0.5rem', 
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      minHeight: '2.75rem',
                      minWidth: '12.5rem',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#6b96e8';
                      e.currentTarget.style.color = 'black';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#84ADEA';
                      e.currentTarget.style.color = 'black';
                    }}
                  >
                    <img 
                      src="/google-docs-logo.png" 
                      alt="Google Docs"
                      style={{
                        width: '14px',
                        height: '14px',
                        objectFit: 'contain'
                      }}
                    />
                    Google Docs
                  </button>

                  {/* Open in Google Sheets */}
                  <button
                    onClick={openGoogleSheets}
                    disabled={isOpeningSheets}
                    className="content-modal-btn"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      padding: '0.75rem 1rem', 
                      backgroundColor: '#16a34a', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '0.5rem', 
                      cursor: isOpeningSheets ? 'not-allowed' : 'pointer', 
                      opacity: isOpeningSheets ? 0.6 : 1,
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      minHeight: '2.75rem',
                      minWidth: '7.5rem',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isOpeningSheets ? <Clock className="animate-spin" size={14} /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 9v12"/></svg>}
                    {isOpeningSheets ? 'Logging to Sheets...' : 'Open in Google Sheets'}
                  </button>

                  {/* Copy */}
                  <button
                    onClick={copyToClipboard}
                    className="content-modal-btn"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      padding: '0.75rem 1rem', 
                      backgroundColor: '#6b7280', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '0.5rem', 
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      minHeight: '2.75rem',
                      minWidth: '7.5rem',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Copy size={14} /> {showCopiedMessage ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                {/* Clear Generated Content - full width */}
                <button
                  onClick={clearGeneratedContent}
                  className="content-modal-btn"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem',
                    backgroundColor: '#fef2f2', border: '0.0625rem solid #fecaca', borderRadius: '0.5rem',
                    color: '#dc2626', cursor: 'pointer', width: '100%', justifyContent: 'center'
                  }}
                >
                  <X size={14} /> Clear Generated Content
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Close backdrop wrapper */}
      </div>

      {/* Variables Menu */}
      <VariablesMenu
        showVariablesMenu={showVariablesMenu}
        variablesButtonPosition={variablesButtonPosition}
        variablesMenuRef={variablesMenuRef}
        activePromptField={activePromptField}
        variableSearchTerm={variableSearchTerm}
        setVariableSearchTerm={setVariableSearchTerm}
        setShowVariablesMenu={setShowVariablesMenu}
        brandKit={resolvedBrandKit}
        insertVariable={insertVariable}
      />

      {/* CMS Integration Modal Component */}
      {showCMSModal && (
        <div 
          onClick={() => setShowCMSModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '31.25rem',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Publish Content to CMS</h3>
              <button
                onClick={() => setShowCMSModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { name: 'Shopify', description: 'Publish directly to Shopify', logo: '/shopify-logo.png' }
                ].map((cms) => (
                  <div
                    key={cms.name}
                    style={{
                      border: '0.0625rem solid #e5e7eb',
                      borderRadius: '0.75rem',
                      padding: '1.25rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'white',
                      boxShadow: '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05)'
                    }}
                    onClick={() => {
                      setShowCustomCMSForm(true);
                      setShowCMSModal(false);
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.boxShadow = '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '1rem',
                      marginBottom: '0.5rem'
                    }}>
                      {cms.logo ? (
                        <div style={{
                          width: '2rem',
                          height: '2rem',
                          borderRadius: '0.5rem',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f8fafc',
                          border: '0.0625rem solid #f1f5f9',
                          flexShrink: 0
                        }}>
                          <img 
                            src={cms.logo} 
                            alt={`${cms.name} logo`}
                            style={{
                              width: '1.5rem',
                              height: '1.5rem',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `<span style="font-size: 0.75rem; font-weight: 600; color: #64748b;">${cms.name.charAt(0)}</span>`;
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{ 
                          width: '2rem',
                          height: '2rem',
                          borderRadius: '0.5rem',
                          backgroundColor: '#6366f1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <span style={{
                            color: 'white',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}>
                            {cms.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <h4 style={{ 
                        fontWeight: '600', 
                        margin: 0,
                        fontSize: '1rem',
                        color: '#111827'
                      }}>
                        {cms.name}
                      </h4>
                    </div>
                    <p style={{ 
                      color: '#6b7280', 
                      fontSize: '0.875rem', 
                      margin: 0,
                      paddingLeft: '3rem',
                      lineHeight: '1.5'
                    }}>
                      {cms.description}
                    </p>
                  </div>
                ))}
              </div>

              {/* Coming Soon Message */}
              {showComingSoonMessage && (
                <div style={{ 
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1.5rem 2rem',
                  backgroundColor: '#EBF212',
                  color: 'black',
                  borderRadius: '0.75rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  boxShadow: '0 0.625rem 1.5625rem -0.1875rem rgba(0, 0, 0, 0.1), 0 0.25rem 0.375rem -0.125rem rgba(0, 0, 0, 0.05)',
                  zIndex: 10002,
                  minWidth: '15.625rem',
                  textAlign: 'center', 
                  animation: 'fadeInScale 0.2s ease-out'
                }}>
                  {showComingSoonMessage} integration coming soon!
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom CMS Form Modal */}
      {showCustomCMSForm && (
        <div 
          onClick={() => {
            setShowCustomCMSForm(false);
            setPublishResult(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '31.25rem',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
                🛍️ Shopify Integration
              </h3>
              <button
                onClick={() => {
                  setShowCustomCMSForm(false);
                  setPublishResult(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            {!publishResult ? (
              <>
                <div style={{ 
                  backgroundColor: '#f0f9ff', 
                  border: '1px solid #bae6fd', 
                  borderRadius: '0.5rem', 
                  padding: '0.75rem', 
                  marginBottom: '1.5rem',
                  fontSize: '0.875rem',
                  color: '#0c4a6e'
                }}>
                  <strong>📌 Setup Guide:</strong>
                  <ol style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
                    <li>Go to Shopify Admin → Apps → Develop apps → Create an app</li>
                    <li>Configure Admin API scopes: <code>write_content</code></li>
                    <li>Install app and copy the Admin API access token</li>
                    <li>Get your Blog ID from: Online Store → Blog Posts → URL</li>
                  </ol>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    Shop Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>https://</span>
                    <input
                      type="text"
                      value={customCMSConfig.shop_name}
                      onChange={(e) => setCustomCMSConfig({...customCMSConfig, shop_name: e.target.value})}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        border: '0.0625rem solid #d1d5db',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                      placeholder="your-store"
                    />
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>.myshopify.com</span>
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    Admin API Access Token <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="password"
                    value={customCMSConfig.access_token}
                    onChange={(e) => setCustomCMSConfig({...customCMSConfig, access_token: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '0.0625rem solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                    placeholder="shpat_xxxxxxxxxxxxxxxxxxxxx"
                  />
                </div>

                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    Blog ID <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={customCMSConfig.blog_id}
                    onChange={(e) => setCustomCMSConfig({...customCMSConfig, blog_id: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '0.0625rem solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                    placeholder="123456789"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', marginBottom: 0 }}>
                    Find this in your blog URL: /admin/blogs/<strong>123456789</strong>
                  </p>
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '1rem',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => publishToCustomCMS('draft')}
                    disabled={isPublishing}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: '500',
                      cursor: isPublishing ? 'not-allowed' : 'pointer',
                      opacity: isPublishing ? 0.6 : 1
                    }}
                  >
                    {isPublishing ? 'Publishing...' : 'Save as Draft'}
                  </button>
                  
                  <button
                    onClick={() => publishToCustomCMS('published')}
                    disabled={isPublishing}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: '500',
                      cursor: isPublishing ? 'not-allowed' : 'pointer',
                      opacity: isPublishing ? 0.6 : 1
                    }}
                  >
                    {isPublishing ? 'Publishing...' : 'Publish Live'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center' }}>
                {publishResult.success ? (
                  <div>
                    <div style={{ 
                      fontSize: '3rem', 
                      marginBottom: '1rem',
                      color: '#10b981'
                    }}>
                      ✅
                    </div>
                    <h4 style={{ 
                      color: '#10b981', 
                      marginBottom: '1rem',
                      fontSize: '1.25rem',
                      fontWeight: '600'
                    }}>
                      Content Published Successfully!
                    </h4>
                    {publishResult.demo_mode && (
                      <div style={{ 
                        backgroundColor: '#fef3c7', 
                        padding: '1rem', 
                        borderRadius: '0.5rem',
                        marginBottom: '1.5rem',
                        border: '0.0625rem solid #f59e0b'
                      }}>
                        <p style={{ 
                          color: '#92400e', 
                          margin: 0,
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}>
                          🛍️ Demo Mode: This is a simulated Shopify publish for demonstration purposes.
                        </p>
                      </div>
                    )}
                    <div style={{ 
                      backgroundColor: '#f9fafb', 
                      padding: '1.5rem', 
                      borderRadius: '0.5rem',
                      textAlign: 'left',
                      marginBottom: '1.5rem'
                    }}>
                      <h5 style={{ margin: '0 0 1rem 0', color: '#374151', fontSize: '1rem' }}>Publication Details:</h5>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        <p style={{ margin: '0.5rem 0' }}><strong>Platform:</strong> {publishResult.cms_type === 'shopify' ? 'Shopify' : publishResult.cms_type}</p>
                        {publishResult.shop_name && (
                          <p style={{ margin: '0.5rem 0' }}><strong>Store:</strong> {publishResult.shop_name}.myshopify.com</p>
                        )}
                        {publishResult.blog_id && (
                          <p style={{ margin: '0.5rem 0' }}><strong>Blog ID:</strong> {publishResult.blog_id}</p>
                        )}
                        <p style={{ margin: '0.5rem 0' }}><strong>Status:</strong> {publishResult.status}</p>
                        <p style={{ margin: '0.5rem 0' }}><strong>Article ID:</strong> {publishResult.id}</p>
                        {publishResult.admin_url && (
                          <p style={{ margin: '0.5rem 0' }}>
                            <strong>Admin URL:</strong> 
                            <a 
                              href={publishResult.admin_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#3b82f6', 
                                textDecoration: 'underline',
                                marginLeft: '0.5rem',
                                wordBreak: 'break-all'
                              }}
                            >
                              View in Shopify Admin
                            </a>
                          </p>
                        )}
                        {publishResult.url && (
                          <p style={{ margin: '0.5rem 0' }}>
                            <strong>Public URL:</strong> 
                            <a 
                              href={publishResult.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#3b82f6', 
                                textDecoration: 'underline',
                                marginLeft: '0.5rem',
                                wordBreak: 'break-all'
                              }}
                            >
                              {publishResult.url}
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => {
                        setShowCustomCMSForm(false);
                        setPublishResult(null);
                      }}
                      style={{
                        padding: '0.75rem 2rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <div>
                    <div style={{ 
                      fontSize: '3rem', 
                      marginBottom: '1rem',
                      color: '#dc2626'
                    }}>
                      ❌
                    </div>
                    <h4 style={{ 
                      color: '#dc2626', 
                      marginBottom: '1rem',
                      fontSize: '1.25rem',
                      fontWeight: '600'
                    }}>
                      Publication Failed
                    </h4>
                    <p style={{ 
                      color: '#6b7280', 
                      marginBottom: '2rem',
                      fontSize: '0.875rem'
                    }}>
                      {publishResult.error || 'An unknown error occurred'}
                    </p>
                    
                    <button
                      onClick={() => setPublishResult(null)}
                      style={{
                        padding: '0.75rem 2rem',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        marginRight: '1rem'
                      }}
                    >
                      Try Again
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowCustomCMSForm(false);
                        setPublishResult(null);
                      }}
                      style={{
                        padding: '0.75rem 2rem',
                        backgroundColor: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Validation Error Modal */}
      {showValidationError && (
        <div 
          onClick={() => setShowValidationError(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: '1rem'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '28rem',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '3rem', 
                marginBottom: '1rem'
              }}>
                ⚠️
              </div>
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                marginBottom: '1rem',
                color: '#111827'
              }}>
                Missing Required Fields
              </h3>
              <p style={{ 
                color: '#6b7280', 
                marginBottom: '1.5rem',
                fontSize: '0.875rem',
                lineHeight: '1.5'
              }}>
                Please fill in all Shopify credentials:<br/>
                <strong>Shop Name</strong>, <strong>Access Token</strong>, and <strong>Blog ID</strong>
              </p>
              <button
                onClick={() => setShowValidationError(false)}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#3b82f6';
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Client ID Error Modal */}
      {showGoogleClientIdError && (
        <div 
          onClick={() => setShowGoogleClientIdError(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: '1rem'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '28rem',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '3rem', 
                marginBottom: '1rem'
              }}>
                🔑
              </div>
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                marginBottom: '1rem',
                color: '#111827'
              }}>
                Google Client ID Not Configured
              </h3>
              <p style={{ 
                color: '#6b7280', 
                marginBottom: '1.5rem',
                fontSize: '0.875rem',
                lineHeight: '1.5'
              }}>
                The Google Client ID is not configured for this feature.
              </p>
              <button
                onClick={() => setShowGoogleClientIdError(false)}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#3b82f6';
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PAAContentActionModal;


